import { getAssignmentCode, isInventoryActionBlocked, isInventoryReassignAllowed } from "./inventoryAssignment";
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { FiMap } from "react-icons/fi";
import { LuSatellite } from "react-icons/lu";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { isRTL } from "../../utils/rtl";
import CommonButton from "../common/CommonButton";
import SearchFieldPicker from "../common/SearchFieldPicker";
import TableScrollShell from "../common/TableScrollShell";
import PageSizeSelector from "../common/PageSizeSelector";
import PagerArrowButton from "../common/PagerArrowButton";
import FilterSelect from "../common/FilterSelect";
import {
  Trash2,
  ArrowLeft,
  Columns3,
  Eye,
  Hash,
  Filter,
  GripVertical,
  History,
  Pencil,
  Inbox,
  LayoutGrid,
  MapPin,
  Plus,
  RotateCcw,
  Repeat2,
  Rows3,
  Search,
  SlidersHorizontal,
  X,
  Users,
  User,
  Barcode,
  Calendar,
  Factory,
  ArrowLeftRight,
  Upload,
  CircleCheck,
  PackageCheck,
  UserCheck,
  Truck,
  XCircle,
  AlertTriangle,
  ShieldAlert,
  HelpCircle,
  Archive,
  Hand,
  Webhook,
  Cpu,
  Download,
} from "lucide-react";
import ModalCloseButton from "../common/ModalCloseButton";
import { SealChips, SealInfoIcon, SealNumbersPanel } from "./SealNumberChips";
import { fetchInventoryItems, fetchInventoryColumnValues, fetchInventoryColumns } from "../../service/MeterInventoryManagement";
import { Spinner } from "../../Spinner";
import ColumnsCustomizeModal from "../common/ColumnsCustomizeModal";
import { toast } from "../../utils/toast";
import ActionIconButton from "../common/ActionIconButton";
import CommonTooltip from "../common/CommonTooltip";
import InventoryTransferModal from "./InventoryTransferModal";
import InventoryHistoryDrawer from "./InventoryHistoryDrawer";
import InventoryExportModal from "./InventoryExportModal";
import InventoryItemEditForm from "./InventoryItemEditForm";
import { isSuperAdmin } from "../../utils/permissions";
import {
  apiErrorMessage,
  formatSealRange,
  getInventoryPermissions,
  isSealType,
  itemTypeOptionsFor,
  sealNumberList,
  sealStockSummary,
  useInventoryScope,
} from "./inventoryFormShared";
import { getStoreByIdApi } from "../../service/Inventory";
import { getUserInfoAPI } from "../../service/User";

const INVENTORY_DEFAULT_COLUMNS = [
  { key: "identifier", label: "Identifier" },
  { key: "itemType", label: "Item Type" },
  { key: "storeId", label: "Store" },
  { key: "location", label: "Location" },
  { key: "groupAssignment", label: "Group Assignment" },
  { key: "status", label: "Status" },
  { key: "source", label: "Source" },
  { key: "createdAt", label: "Created At" },
];
const INVENTORY_COLUMN_LABEL_KEYS = {
  itemType: "inventoryColItemType",
  identifier: "inventoryColIdentifier",
  status: "inventoryColStatus",
  components: "inventoryColComponents",
  groupAssignment: "inventoryColGroupAssignment",
  conditionNote: "inventoryColConditionNote",
  createdBy: "inventoryColCreatedBy",
  createdAt: "inventoryColCreatedAt",
  updatedBy: "inventoryColUpdatedBy",
  updatedAt: "inventoryColUpdatedAt",
  storeId: "inventoryColStore",
  source: "inventoryColSource",
  location: "inventoryColLocation",
  latitude: "inventoryColLatitude",
  longitude: "inventoryColLongitude",
  transfer_from: "inventoryColTransferFrom",
  transfer_to: "inventoryColTransferTo",
  sealNumbers: "inventorySealNumbers",
  seal_numbers: "inventorySealNumbers",
  sealPrefix: "inventorySealPrefix",
  sealFrom: "inventorySealFrom",
  sealTo: "inventorySealTo",
  sealCount: "inventorySealCount",
  sealRange: "inventorySealRange",
};
// Columns/fields that hold a list of seal numbers, shown as chips rather than plain text.
const SEAL_LIST_KEYS = new Set(["sealNumbers", "seal_numbers", "usedSealNumbers"]);
// Placeholder shown in table cells (and the matching details panel) when a value is missing.
const EMPTY_VALUE = "-";
const USER_ID_COLUMNS = ["createdBy", "updatedBy"];
const DETAIL_ICONS = {
  storeId: MapPin,
  location: MapPin,
  storeLocation: MapPin,
  latitude: MapPin,
  longitude: MapPin,
  group: Users,
  groupAssignment: Users,
  assignedTo: User,
  createdBy: User,
  updatedBy: User,
  meterBarcode: Barcode,
  meterMake: Factory,
};
const detailIconFor = (key) =>
  DETAIL_ICONS[key] || (/(date|At|year)$/i.test(key) ? Calendar : Hash);
const isNumericId = (value) => /^\d+$/.test(String(value ?? ""));
const INVENTORY_TABLE_LAYOUT_KEY = "inventoryTableLayout";
const INVENTORY_TABLE_FILTERS_KEY = "inventoryTableFilters";

// Session first (per-tab, survives refresh), local as a fallback so a full
// browser restart doesn't lose it either -- same dual-write TaskListView uses
// for its own table filters.
const readInventoryTableFilters = () => {
  try {
    const raw = sessionStorage.getItem(INVENTORY_TABLE_FILTERS_KEY) || localStorage.getItem(INVENTORY_TABLE_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    console.error("Error reading inventory table filters:", error);
    return null;
  }
};

const storePin = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -36],
  shadowSize: [41, 41],
});

const STORE_LOCATION_ZOOM = 7;

const toCoordinate = (value, limit) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
};

const ResizeMapOnOpen = () => {
  const map = useMap();
  useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(), 250);
    return () => window.clearTimeout(timer);
  }, [map]);
  return null;
};

const StoreLocationButton = ({ onOpen, tooltipText }) => (
  <CommonTooltip text={tooltipText} position="top" disableDefaultDescription className="shrink-0">
    <button
      type="button"
      aria-label={tooltipText}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      className="inline-flex items-center justify-center w-5 h-5 rounded shrink-0 text-slate-400 hover:text-[#236DB4] hover:bg-blue-50 transition-colors"
    >
      <MapPin size={13} />
    </button>
  </CommonTooltip>
);

const StoreLocationModal = ({ location, onClose, t, isRtl }) => {
  const [map, setMap] = useState(null);
  const [mapType, setMapType] = useState("map");
  const [zoom, setZoom] = useState(STORE_LOCATION_ZOOM);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!map) return undefined;
    const onZoom = () => setZoom(map.getZoom());
    map.on("zoomend", onZoom);
    return () => map.off("zoomend", onZoom);
  }, [map]);

  const { latitude, longitude, label } = location;
  const position = [latitude, longitude];

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4" dir={isRtl ? "rtl" : "ltr"}>
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label={t.inventoryClosePanel}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative flex w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-800">{t.inventoryCurrentLocation}</h2>
            <p className="mt-0.5 flex min-w-0 items-center gap-1 text-sm text-slate-500">
              {label && <span className="truncate">{label}</span>}
              <span className="shrink-0 font-mono text-xs" dir="ltr">
                ({latitude}, {longitude})
              </span>
            </p>
          </div>
          <ModalCloseButton onClick={onClose} className="shrink-0" ariaLabel={t.inventoryClosePanel} />
        </div>
        <div className="p-4 sm:p-5">
          <div
            className="relative h-[55dvh] min-h-[320px] max-h-[560px] w-full overflow-hidden rounded-lg border border-slate-200"
            dir="ltr"
          >
            <MapContainer
              ref={setMap}
              center={position}
              zoom={STORE_LOCATION_ZOOM}
              zoomControl={false}
              scrollWheelZoom
              className="h-full w-full"
              style={{ zIndex: 0 }}
            >
              {mapType === "map" ? (
                <TileLayer
                  attribution="© OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              ) : (
                <TileLayer
                  attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
                  url="https://mt.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                />
              )}
              <ResizeMapOnOpen />
              <Marker position={position} icon={storePin}>
                {label && <Popup>{label}</Popup>}
              </Marker>
            </MapContainer>
            <div className="absolute left-2 top-2 z-[1000] flex w-10 flex-col items-center rounded-lg border border-slate-200 bg-white text-slate-800 shadow-md">
              <CommonTooltip text={t.storeZoomIn} position="right" disableDefaultDescription>
                <button type="button" onClick={() => map?.zoomIn()} aria-label={t.storeZoomIn} className="flex h-10 w-10 items-center justify-center rounded-t-lg text-lg font-medium hover:bg-slate-100">+</button>
              </CommonTooltip>
              <span className="flex h-8 w-10 items-center justify-center border-y border-slate-200 text-xs font-medium" aria-live="polite">{zoom}x</span>
              <CommonTooltip text={t.storeZoomOut} position="right" disableDefaultDescription>
                <button type="button" onClick={() => map?.zoomOut()} aria-label={t.storeZoomOut} className="flex h-10 w-10 items-center justify-center rounded-b-lg text-lg font-medium hover:bg-slate-100">−</button>
              </CommonTooltip>
            </div>
            <div className="absolute right-2 top-2 z-[1000] flex rounded-lg border border-slate-200 bg-white p-1 shadow-md" role="group" aria-label={t.storeMapView}>
              <button type="button" onClick={() => setMapType("map")} aria-pressed={mapType === "map"} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${mapType === "map" ? "bg-[#236DB4] text-white" : "text-slate-700 hover:bg-slate-100"}`}><FiMap size={14} aria-hidden="true" />{t.storeDefaultView}</button>
              <button type="button" onClick={() => setMapType("satellite")} aria-pressed={mapType === "satellite"} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${mapType === "satellite" ? "bg-[#236DB4] text-white" : "text-slate-700 hover:bg-slate-100"}`}><LuSatellite size={15} aria-hidden="true" />{t.storeSatelliteView}</button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const EDGE_MARGIN = 10;
const GAP = 8;
const CLOSE_DELAY_MS = 150;

const normalizeGroupDetails = (details) => {
  if (!details || typeof details !== "object") return null;
  const members = (Array.isArray(details.groupUsersList) ? details.groupUsersList : []).map((m) => ({
    id: m?.userId ?? m?.id,
    name: m?.userName ?? m?.name ?? "",
    profilePicture: m?.profilePicture || null,
  }));
  return {
    groupId: details.groupId ?? null,
    groupName: details.groupName ?? "",
    count: Number.isFinite(Number(details.numberOfUser)) ? Number(details.numberOfUser) : members.length,
    members,
  };
};

const nameToHslColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360} 55% 45%)`;
};

const MemberAvatar = ({ member, className }) =>
  member.profilePicture ? (
    <img src={member.profilePicture} alt="" className={`${className} object-cover`} />
  ) : (
    <div
      className={`${className} flex items-center justify-center font-semibold text-white`}
      style={{ background: nameToHslColor(member.name || "?") }}
    >
      {(member.name || "?")[0].toUpperCase()}
    </div>
  );

const MembersPopover = ({ anchorRef, rtl, onMouseEnter, onMouseLeave, children }) => {
  const popRef = useRef(null);
  const [style, setStyle] = useState({ position: "fixed", visibility: "hidden" });
  // Which edge of the popup faces the trigger, and where along it the arrow tip lands.
  const [arrow, setArrow] = useState({ below: true, left: 24 });

  useLayoutEffect(() => {
    const update = () => {
      if (!anchorRef.current || !popRef.current) return;
      const trigger = anchorRef.current.getBoundingClientRect();
      const pop = popRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - trigger.bottom - GAP - EDGE_MARGIN;
      const spaceAbove = trigger.top - GAP - EDGE_MARGIN;
      const openBelow = pop.height <= spaceBelow || (pop.height > spaceAbove && spaceBelow >= spaceAbove);
      const top = openBelow
        ? trigger.bottom + GAP
        : Math.max(EDGE_MARGIN, trigger.top - GAP - pop.height);

      const preferredLeft = rtl ? trigger.right - pop.width : trigger.left;
      const maxLeft = Math.max(EDGE_MARGIN, window.innerWidth - pop.width - EDGE_MARGIN);
      const left = Math.min(Math.max(preferredLeft, EDGE_MARGIN), maxLeft);

      setStyle({ position: "fixed", top, left, visibility: "visible" });
      // Arrow points at the middle of the trigger, kept clear of the rounded corners.
      setArrow({
        below: openBelow,
        left: Math.min(Math.max(trigger.left + trigger.width / 2 - left, 16), Math.max(16, pop.width - 16)),
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef, rtl]);

  return createPortal(
    <div
      ref={popRef}
      style={style}
      dir={rtl ? "rtl" : "ltr"}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="z-[1100] w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
    >
      <span
        aria-hidden="true"
        className={`absolute h-2.5 w-2.5 -translate-x-1/2 rotate-45 border border-slate-200 bg-white ${
          arrow.below ? "-top-[6px] border-b-0 border-r-0" : "-bottom-[6px] border-l-0 border-t-0"
        }`}
        style={{ left: arrow.left }}
      />
      {children}
    </div>,
    document.body
  );
};

const GroupMembersPreview = ({ details, label, rtl }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const show = () => {
    clearTimeout(closeTimerRef.current);
    setOpen(true);
  };
  const hide = () => {
    closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  const { members, count } = details;
  if (!members.length) return <span className="text-xs text-slate-400">—</span>;
  const shown = members.slice(0, 2);
  const extra = Math.max(count, members.length) - shown.length;

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex items-center gap-1.5"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <div className="flex shrink-0 items-center">
        {shown.map((m, i) => (
          <MemberAvatar
            key={`${m.id}-${i}`}
            member={m}
            className={`h-5 w-5 rounded-full border-2 border-white text-[10px] ${i > 0 ? (rtl ? "-mr-1.5" : "-ml-1.5") : ""}`}
          />
        ))}
      </div>
      {extra > 0 && (
        <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">
          +{extra}
        </span>
      )}
      {open && (
        <MembersPopover anchorRef={triggerRef} rtl={rtl} onMouseEnter={show} onMouseLeave={hide}>
          <div className="px-1.5 pb-1.5 text-start text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {label} ({Math.max(count, members.length)})
          </div>
          <div className="custom-scrollbar max-h-48 space-y-1 overflow-y-auto overflow-x-hidden">
            {members.map((m, i) => (
              <div
                key={`${m.id}-${i}`}
                className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm text-slate-700"
              >
                <MemberAvatar member={m} className="h-4 w-4 shrink-0 rounded-full text-[9px]" />
                <span className="min-w-0 flex-1 break-words text-start">{m.name}</span>
              </div>
            ))}
          </div>
        </MembersPopover>
      )}
    </div>
  );
};

const InventoryGroupCell = ({ details, fallbackName, membersLabel, rtl, renderName }) => {
  const name = details?.groupName || fallbackName;
  if (!name) return <span>{EMPTY_VALUE}</span>;

  return (
    <div className="flex w-full min-w-0 flex-col items-center py-0.5 text-center">
      <div className="w-full min-w-0 text-sm font-medium text-slate-800">{renderName(name)}</div>
      {details && (
        <div className="mt-1 min-w-0 max-w-full">
          <GroupMembersPreview details={details} label={membersLabel} rtl={rtl} />
        </div>
      )}
    </div>
  );
};

const InventoryCellText = ({ text }) => {
  const outerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer) return undefined;
    const measure = () => {
      const inner = outer.querySelector("[data-cell-text]");
      setIsOverflowing(Boolean(inner && inner.scrollWidth > inner.clientWidth));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    return () => observer.disconnect();
  }, [text]);

  const label = (
    <span data-cell-text className="block w-full truncate">
      {text}
    </span>
  );

  return (
    <span ref={outerRef} className="block w-full min-w-0">
      {isOverflowing ? (
        <CommonTooltip text={String(text ?? "")} position="top" disableDefaultDescription className="block w-full min-w-0">
          {label}
        </CommonTooltip>
      ) : (
        label
      )}
    </span>
  );
};

const HeaderFilterPopover = ({ active, children, width = 232, tooltipText }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.min(Math.max(8, r.left - width + 20), window.innerWidth - width - 8);
    setPos({ left, top: r.bottom + 6 });
  };

  const openPanel = () => {
    place();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => place();
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  const panel =
    open && pos
      ? createPortal(
          <div
            ref={popRef}
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[10060] bg-white border border-slate-200 rounded-lg shadow-xl normal-case font-normal tracking-normal p-3"
            style={{ left: pos.left, top: pos.top, width }}
          >
            {typeof children === "function" ? children(() => setOpen(false)) : children}
          </div>,
          document.body
        )
      : null;

  return (
    <span className="relative inline-flex shrink-0">
      <CommonTooltip text={tooltipText} position="top" disableDefaultDescription className="shrink-0">
        <button
          ref={btnRef}
          type="button"
          aria-label={tooltipText}
          onClick={(e) => {
            e.stopPropagation();
            open ? setOpen(false) : openPanel();
          }}
          className={`inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors ${
            active ? "text-[#236DB4] bg-blue-50" : "text-[#236DB4] hover:bg-blue-50"
          }`}
        >
          <Filter size={12} fill={active ? "currentColor" : "none"} />
        </button>
      </CommonTooltip>
      {panel}
    </span>
  );
};

const FILTER_OPTION_ROW_HEIGHT_PX = 28;
const FILTER_OPTION_VIEWPORT_HEIGHT_PX = 192;
const FILTER_OPTION_OVERSCAN_ROWS = 5;
const MAX_COLUMN_FILTERS = 15;
const BLANK_FILTER_VALUE = "";

const columnKeyToApiColumn = (key) => {
  const map = {
    meterNumber: "identifier",
    meterType: "meterType",
    storeLocation: "location",
    group: "groupAssignment",
    assignment: "assignedTo",
    status: "status",
    simNo: "simNo",
    modemNo: "modemNo",
    installationDate: "installationDate",
    createdAt: "createdAt",
    // The columns API sends "components", but values are stored under metadata.meterComponents.
    components: "meterComponents",
  };
  return map[key] || key;
};

const makeInFilter = (column, values) => ({
  column: columnKeyToApiColumn(column),
  op: "in",
  values: Array.isArray(values) ? values.map((value) => String(value ?? "")) : [],
});

const makeContainsFilter = (column, value) => ({
  column: columnKeyToApiColumn(column),
  op: "contains",
  value: String(value ?? ""),
});

const isMeaningfulColumnFilter = (filter) => {
  if (!filter || !filter.column) return false;
  if (filter.op === "in") return Array.isArray(filter.values) && filter.values.length > 0;
  if (filter.op === "contains") return String(filter.value ?? "").trim() !== "";
  return false;
};

const serializeColumnFilters = (filtersMap) => {
  const serialized = Object.values(filtersMap || {})
    .filter(isMeaningfulColumnFilter)
    .map((filter) => {
      const column = filter.column;
      if (filter.op === "in") return { column, op: "in", values: filter.values };
      return { column, op: "contains", value: String(filter.value).trim() };
    });
  return serialized.slice(0, MAX_COLUMN_FILTERS);
};

// { id: name } for options whose label differs from their value.
const labelledOptions = (list) =>
  Object.fromEntries(
    (list || [])
      .filter((item) => item && typeof item === "object" && item.value != null && item.label != null && String(item.label) !== String(item.value))
      .map((item) => [String(item.value), String(item.label)])
  );

const countActiveColumnFilters = (filtersMap) =>
  Object.values(filtersMap || {}).filter(isMeaningfulColumnFilter).length;

const ColumnValueFilterList = ({
  column,
  selected,
  containsValue,
  onApply,
  onApplyContains,
  searchPlaceholder,
  containsPlaceholder,
  applyLabel,
  clearLabel,
  selectAllLabel,
  selectNoneLabel,
  noValuesLabel,
  loadingLabel,
  blanksLabel,
  containsHelpText,
  staticOptions,
  // Lazy static lists: staticLoading keeps the spinner up until the parent's fetch lands,
  // and onOpen tells the parent this column's popover was opened so it can fetch.
  staticLoading = false,
  onOpen,
}) => {
  // Filter values are scoped like the rows they filter, and a super admin's
  // organization cannot be inferred from the token.
  const { organizationId, projectId } = useInventoryScope();
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [sourceUnavailable, setSourceUnavailable] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [draft, setDraft] = useState(null);
  const [fetchedLabelByValue, setFetchedLabelByValue] = useState(() => new Map());
 
  const selectedKey = (selected || []).join("");
  const staticLabelByValue = useMemo(
    () => new Map((staticOptions || []).map((option) => [option.value, option.label])),
    [staticOptions]
  );

  useEffect(() => {
    onOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!staticOptions) return;
    if (staticLoading) {
      setOptionsLoading(true);
      return;
    }
    const q = String(query ?? "").trim().toLowerCase();
    const values = staticOptions
      .filter(
        (option) =>
          !q ||
          String(option.label).toLowerCase().includes(q) ||
          String(option.value).toLowerCase().includes(q)
      )
      .map((option) => option.value);
    setSourceUnavailable(false);
    setOptions(values);
    const preselected = selected || [];
    setDraft(new Set(preselected.length ? preselected : values));
    setOptionsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticOptions, staticLoading, query, selectedKey]);

  useEffect(() => {
    if (staticOptions) return;
    let cancelled = false;
    setOptionsLoading(true);

    fetchInventoryColumnValues({
      column,
      ...(organizationId ? { organizationId: Number(organizationId) } : {}),
      ...(projectId ? { projectIds: [Number(projectId)] } : {}),
      ...(String(query ?? "").trim() ? { search: String(query).trim() } : {}),
    })
      .then((raw) => {
        if (cancelled) return;
        const list = Array.isArray(raw) ? raw : raw?.values || [];
        const labels = new Map();
        const values = list
          .map((item) => {
            if (item === null || item === undefined) return "";
            if (typeof item === "object") {
              const value = String(item.value ?? item.label ?? item.name ?? "");
              if (item.label != null && String(item.label) !== value) labels.set(value, String(item.label));
              return value;
            }
            return String(item);
          })
          .filter((value, index, all) => all.indexOf(value) === index);
        setFetchedLabelByValue(labels);
        const withBlanks = values.length ? [BLANK_FILTER_VALUE, ...values.filter((v) => v !== BLANK_FILTER_VALUE)] : [];
        setSourceUnavailable(withBlanks.length === 0 && !String(query ?? "").trim());
        setOptions(withBlanks);
        const preselected = selected || [];
        setDraft(new Set(preselected.length ? preselected : withBlanks));
        setOptionsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setOptions([]);
        setSourceUnavailable(true);
        setDraft(new Set());
        setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column, query, selectedKey]);

  if (!optionsLoading && sourceUnavailable) {
    return (
      <ColumnContainsFilter
        value={containsValue}
        onApply={onApplyContains}
        placeholder={containsPlaceholder}
        clearLabel={clearLabel}
        applyLabel={applyLabel}
        helpText={containsHelpText}
      />
    );
  }

  const toggleValue = (value) => {
    setDraft((prev) => {
      const next = new Set(prev || []);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };

  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / FILTER_OPTION_ROW_HEIGHT_PX) - FILTER_OPTION_OVERSCAN_ROWS
  );
  const endIndex = Math.min(
    options.length,
    Math.ceil((scrollTop + FILTER_OPTION_VIEWPORT_HEIGHT_PX) / FILTER_OPTION_ROW_HEIGHT_PX) +
      FILTER_OPTION_OVERSCAN_ROWS
  );
  const windowedOptions = options.slice(startIndex, endIndex);

  return (
    <div className="w-full">
      <div className="relative mb-2">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full pl-6 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
        />
      </div>
      <div
        className="overflow-y-auto -mx-1 px-1"
        style={{ height: FILTER_OPTION_VIEWPORT_HEIGHT_PX }}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        {optionsLoading && (
          <p className="text-xs text-slate-400 px-2 py-3 text-center">{loadingLabel}</p>
        )}
        {!optionsLoading && options.length === 0 && (
          <p className="text-xs text-slate-400 px-2 py-3 text-center">{noValuesLabel}</p>
        )}
        {!optionsLoading && options.length > 0 && (
          <div style={{ height: options.length * FILTER_OPTION_ROW_HEIGHT_PX, position: "relative" }}>
            {windowedOptions.map((value, index) => {
              const rowIndex = startIndex + index;
              const label =
                value === BLANK_FILTER_VALUE
                  ? blanksLabel
                  : staticLabelByValue.get(value) ?? fetchedLabelByValue.get(value) ?? value;
              return (
                <label
                  key={`${value}-${rowIndex}`}
                  className="absolute inset-x-0 flex items-center gap-2 px-2 hover:bg-slate-50 rounded cursor-pointer text-xs"
                  style={{
                    top: rowIndex * FILTER_OPTION_ROW_HEIGHT_PX,
                    height: FILTER_OPTION_ROW_HEIGHT_PX,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(draft?.has(value))}
                    onChange={() => toggleValue(value)}
                    className="accent-[#236DB4] w-3.5 h-3.5 shrink-0"
                  />
                  <span className={`truncate ${value === BLANK_FILTER_VALUE ? "italic text-slate-400" : ""}`}>
                    {label}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100">
        <div className="flex gap-2">
          <button type="button" onClick={() => setDraft(new Set(options))} className="text-[11px] font-medium text-[#236DB4] hover:underline">
            {selectAllLabel}
          </button>
          <button type="button" onClick={() => setDraft(new Set())} className="text-[11px] font-medium text-slate-400 hover:underline">
            {selectNoneLabel}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            const picked = draft ? [...draft] : [];
            if (picked.length === 0 || picked.length === options.length) onApply([]);
            else onApply(picked);
          }}
          className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded hover:bg-[#1d5a96]"
        >
          {applyLabel}
        </button>
      </div>
      <button
        type="button"
        onClick={() => {
          setDraft(new Set());
          onApply([]);
        }}
        className="sr-only"
      >
        {clearLabel}
      </button>
    </div>
  );
};

const ColumnContainsFilter = ({ value, onApply, placeholder, clearLabel, applyLabel, helpText }) => {
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  return (
    <div className="w-full">
      <div className="relative mb-2">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onApply(draft);
          }}
          placeholder={placeholder}
          className="w-full pl-6 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
        />
      </div>
      <p className="text-[11px] leading-snug text-slate-400 px-0.5 pb-2">
        {helpText}
      </p>
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => {
            setDraft("");
            onApply("");
          }}
          className="text-[11px] font-medium text-slate-400 hover:underline"
        >
          {clearLabel}
        </button>
        <button
          type="button"
          onClick={() => onApply(draft)}
          className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded hover:bg-[#1d5a96]"
        >
          {applyLabel}
        </button>
      </div>
    </div>
  );
};

export const InventoryManagement = () => {
  const navigate = useNavigate();
  const { theme, langMode } = useContext(AppContext);
  const { projectId, organizationId } = useInventoryScope();
  // Role permissions decide which write actions this screen offers: adding and
  // uploading need create or update, transferring is an update.
  const inventoryPerm = useMemo(() => getInventoryPermissions(), []);
  const canCreateInventory = Boolean(inventoryPerm.canCreate);
  const canUpdateInventory = Boolean(inventoryPerm.canUpdate);
  const canWriteInventory = canCreateInventory || canUpdateInventory;
  // Stores are org-wide reference data, so creating one is super-admin only regardless
  // of the role's inventory create right.
  const canCreateStore = isSuperAdmin();
  const isRtl = isRTL(langMode);
  const t = translations[langMode] || translations.en;
  const [density, setDensity] = useState("compact");
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const columnsButtonRef = useRef(null);
  const [searchFieldKey, setSearchFieldKey] = useState("identifier");
  
  const [searchFieldValues, setSearchFieldValues] = useState({});
  const [appliedSearch, setAppliedSearch] = useState({ key: "identifier", value: "" });

  const [hasHydratedInventoryFilters, setHasHydratedInventoryFilters] = useState(false);
  const [filterStoreId, setFilterStoreId] = useState("");
  const [filterAssignedTo, setFilterAssignedTo] = useState("");
  const [filterGroupAssignment, setFilterGroupAssignment] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [storeLocationView, setStoreLocationView] = useState(null);
  const loadRequestIdRef = useRef(0);
  const [loadError, setLoadError] = useState(false);
  const [historyItems] = useState([]);
  const [transferMode, setTransferMode] = useState("transfer");
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  useEffect(() => {
    setSelectedRowIds([]);
    setTransferModalOpen(false);
  }, [currentPage, pageSize]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [editingDetails, setEditingDetails] = useState(false);
  useEffect(() => {
    if (!drawerOpen) setEditingDetails(false);
  }, [drawerOpen]);
  
  useEffect(() => {
    setActiveRow((prev) => (prev ? rows.find((row) => row.id === prev.id) || prev : prev));
  }, [rows]);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyDrawerItem, setHistoryDrawerItem] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Export notifications link here with `?exportId=`, which opens the export
  // modal; the param is dropped so a refresh doesn't reopen it.
  useEffect(() => {
    if (!searchParams.get("exportId")) return;
    setExportModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("exportId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [columnFilters, setColumnFilters] = useState({});
  const [justRevealedFilters, setJustRevealedFilters] = useState(false);
  const prevShowFiltersRef = useRef(showColumnFilters);
  const [dragColKey, setDragColKey] = useState(null);
  const [dragOverColKey, setDragOverColKey] = useState(null);
  const [inventoryColumns, setInventoryColumns] = useState([]);

  const [inventoryColumnsLoaded, setInventoryColumnsLoaded] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    INVENTORY_DEFAULT_COLUMNS.map((col) => col.key)
  );
  const normalizedColumns = useMemo(() => {
    const base = Array.isArray(inventoryColumns) && inventoryColumns.length
      ? inventoryColumns
      : INVENTORY_DEFAULT_COLUMNS;
    return base
      .map((col) => {
        const key = String(col?.key ?? col?.value ?? "").trim();
        if (!key) return null;
        const group = col?.group ?? "";
        const translatedLabel = group === "metadata" ? null : t[INVENTORY_COLUMN_LABEL_KEYS[key]];
        return {
          key,
          label: String(translatedLabel || (col?.label ?? col?.key ?? key)),
          group,
        };
      })
      .filter(Boolean);
  }, [inventoryColumns, t]);
  const tableHeaders = useMemo(
    () =>
      normalizedColumns.map((col) => ({
        value: col.key,
        label: col.label,
        group: col.group,
      })),
    [normalizedColumns]
  );
  const columnMetaByKey = useMemo(() => {
    const map = new Map();
    normalizedColumns.forEach((col) => {
      map.set(col.key, col);
    });
    return map;
  }, [normalizedColumns]);
  const visibleColumnKeySet = useMemo(() => new Set(visibleColumnKeys), [visibleColumnKeys]);
 
  const orderedVisibleHeaders = useMemo(() => {
    const byKey = new Map(tableHeaders.map((header) => [header.value, header]));
    return visibleColumnKeys.map((key) => byKey.get(key)).filter(Boolean);
  }, [tableHeaders, visibleColumnKeys]);
  const visibleColumns = visibleColumnKeys.length;
  const totalColumns = normalizedColumns.length;
  const formatStatusLabel = (value) => {
    if (!value) return EMPTY_VALUE;
    return String(value)
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());
  };
  const resolveLocale = () => {
    if (langMode === "ar") return "ar";
    if (langMode === "ur") return "ur";
    return "en-US";
  };
  const formatDateValue = (value) => {
    if (!value) return EMPTY_VALUE;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(resolveLocale(), {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };
  const normalizeResponse = (payload, size) => {
    if (!payload) return { items: [], total: 0, pages: 0 };
    if (Array.isArray(payload)) {
      return { items: payload, total: payload.length, pages: 1 };
    }
    const items =
      payload.itemList ||
      payload.meterInventoryList ||
      payload.content ||
      payload.items ||
      payload.data ||
      [];
    const total =
      payload.totalSize ??
      payload.totalSize ??
      payload.totalElements ??
      payload.total ??
      payload.count ??
      payload.totalCount ??
      items.length;
    const pages =
      payload.totalPages ??
      (size ? Math.ceil(total / size) : total ? 1 : 0);
    return { items, total, pages };
  };
  const buildQueryParams = () => {
    const params = {
      page: currentPage,
      size: pageSize,
    };
    if (organizationId) params.organizationId = Number(organizationId);
    if (projectId) params.projectIds = [Number(projectId)];
    if (filterStoreId) params.storeId = Number(filterStoreId);
    if (filterAssignedTo) params.assignedTo = Number(filterAssignedTo);
    if (filterGroupAssignment) params.groupAssignment = filterGroupAssignment;
    if (serializedColumnFilters.length) params.columnFilters = serializedColumnFilters;
    return params;
  };
  // The export covers every row matching the current filters, so paging is left
  // out; only a super admin names the organization.
  const buildExportPayload = () => {
    const { page, size, ...filters } = buildQueryParams();
    if (!isSuperAdmin()) delete filters.organizationId;
    return filters;
  };
  const itemTypeLabels = {
    NEW_METER: t.inventoryTypeNewMeter,
    OLD_METER: t.inventoryTypeOldMeter,
    MODEM: t.inventoryTypeModem,
    SIM_CARD: t.inventoryTypeSimCard,
    SEAL: t.inventoryTypeSeal,
  };
  // Every item type is offered in the filter, so SEAL can be picked before any
  // seal stock shows up in the column values.
  const itemTypeFilterOptions = useMemo(() => itemTypeOptionsFor(t), [t]);
  const statusLabels = {
    AVAILABLE: t.inventoryStatusAvailable,
    ASSIGNED: t.inventoryStatusAssigned,
    REASSIGNED: t.inventoryStatusReassigned,
    IN_TRANSIT: t.inventoryStatusInTransit,
    INSTALLED: t.inventoryStatusInstalled,
    REMOVED: t.inventoryStatusRemoved,
    RETURNED: t.inventoryStatusReturned,
    DAMAGED: t.inventoryStatusDamaged,
    QUARANTINED: t.inventoryStatusQuarantined,
    LOST: t.inventoryStatusLost,
    RETIRED: t.inventoryStatusRetired,
  };
  // Short explanation shown when hovering a status badge.
  const statusTooltips = {
    AVAILABLE: t.inventoryStatusTipAvailable,
    ASSIGNED: t.inventoryStatusTipAssigned,
    REASSIGNED: t.inventoryStatusTipReassigned,
    IN_TRANSIT: t.inventoryStatusTipInTransit,
    INSTALLED: t.inventoryStatusTipInstalled,
    REMOVED: t.inventoryStatusTipRemoved,
    RETURNED: t.inventoryStatusTipReturned,
    DAMAGED: t.inventoryStatusTipDamaged,
    QUARANTINED: t.inventoryStatusTipQuarantined,
    LOST: t.inventoryStatusTipLost,
    RETIRED: t.inventoryStatusTipRetired,
  };
  const statusFilterOptions = useMemo(
    () => [
      { value: "AVAILABLE", label: t.inventoryStatusAvailable },
      { value: "ASSIGNED", label: t.inventoryStatusAssigned },
      { value: "REASSIGNED", label: t.inventoryStatusReassigned },
      { value: "IN_TRANSIT", label: t.inventoryStatusInTransit },
      { value: "INSTALLED", label: t.inventoryStatusInstalled },
      { value: "REMOVED", label: t.inventoryStatusRemoved },
      { value: "RETURNED", label: t.inventoryStatusReturned },
      { value: "DAMAGED", label: t.inventoryStatusDamaged },
      { value: "QUARANTINED", label: t.inventoryStatusQuarantined },
      { value: "LOST", label: t.inventoryStatusLost },
      { value: "RETIRED", label: t.inventoryStatusRetired },
    ],
    [t]
  );
  // Store filter/search list: the store IDs items actually use, labelled with store names.
  const [storeIdsInData, setStoreIdsInData] = useState([]);
  const [storeNamesById, setStoreNamesById] = useState({});
  const requestedStoreIdsRef = useRef(new Set());
  const storeScopeKey = `${organizationId ?? ""}:${projectId ?? ""}`;
  const storeScopeKeyRef = useRef(storeScopeKey);

  useEffect(() => {
    storeScopeKeyRef.current = storeScopeKey;
    requestedStoreIdsRef.current = new Set();
    setStoreNamesById({});
    setStoreValuesLoaded(false);
  }, [storeScopeKey]);

  // The store/user value lists only feed one column's filter popover and the store/user
  // search, so each is fetched when that popover is opened (or that search field is chosen),
  // like any other column's filter values -- not on every page load.
  const [openedValueColumns, setOpenedValueColumns] = useState({});
  const markValueColumnOpened = (column) =>
    setOpenedValueColumns((prev) => (prev[column] ? prev : { ...prev, [column]: true }));
  const needsStoreValues = Boolean(openedValueColumns.storeId) || searchFieldKey === "storeId";
  const neededUserColumns = USER_ID_COLUMNS.filter(
    (column) => openedValueColumns[column] || searchFieldKey === column
  );
  const neededUserColumnsKey = neededUserColumns.join(",");
  const [storeValuesLoaded, setStoreValuesLoaded] = useState(false);
  const fetchedUserColumnsRef = useRef({});

  useEffect(() => {
    if (!needsStoreValues) return undefined;
    let cancelled = false;
    fetchInventoryColumnValues({
      column: "storeId",
      ...(organizationId ? { organizationId: Number(organizationId) } : {}),
      ...(projectId ? { projectIds: [Number(projectId)] } : {}),
    })
      .then((raw) => {
        if (cancelled) return;
        const list = Array.isArray(raw) ? raw : raw?.values || [];
        const ids = list.map((item) => {
          const value = item !== null && typeof item === "object" ? item.value : item;
          return value === null || value === undefined ? BLANK_FILTER_VALUE : String(value);
        });
        const namedStores = labelledOptions(list);
        if (Object.keys(namedStores).length) setStoreNamesById((prev) => ({ ...prev, ...namedStores }));
        setStoreIdsInData([...new Set(ids)]);
        setStoreValuesLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setStoreIdsInData([]);
        setStoreValuesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, projectId, totalItems, needsStoreValues]);

  useEffect(() => {
    setStoreNamesById((prev) => {
      let next = prev;
      rows.forEach(({ raw }) => {
        const id = raw?.storeId;
        const name = raw?.storeName;
        if (id === null || id === undefined || id === "" || !name || prev[String(id)] === name) return;
        if (next === prev) next = { ...prev };
        next[String(id)] = name;
      });
      return next;
    });
  }, [rows]);

  useEffect(() => {
    storeIdsInData
      .filter((id) => id !== BLANK_FILTER_VALUE && !storeNamesById[id] && !requestedStoreIdsRef.current.has(id))
      .forEach((id) => {
        requestedStoreIdsRef.current.add(id);
        const requestScopeKey = storeScopeKey;
        getStoreByIdApi(id)
          .then((res) => {
            if (storeScopeKeyRef.current !== requestScopeKey) return;
            const store = res?.body?.value || res?.data || res;
            const name = store?.storeName ?? store?.name;
            // column-values can return stores outside the current org/project; skip those.
            const sameScope = (field, current) => !current || store?.[field] == null || String(store[field]) === String(current);
            if (!name || !sameScope("organizationId", organizationId) || !sameScope("projectId", projectId)) return;
            setStoreNamesById((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }));
          })
          .catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeIdsInData, storeNamesById]);

  // storeNamesById only holds in-scope stores (from rows, or checked via store/get).
  const hasRowWithoutStore = rows.some(({ raw }) => raw && (raw.storeId === null || raw.storeId === undefined || raw.storeId === ""));
  const includeBlankStore = storeIdsInData.includes(BLANK_FILTER_VALUE) || hasRowWithoutStore;
  const storeFilterOptions = useMemo(
    () => [
      ...(includeBlankStore ? [{ value: BLANK_FILTER_VALUE, label: "" }] : []),
      ...Object.entries(storeNamesById)
        .map(([id, name]) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [includeBlankStore, storeNamesById]
  );

  // User columns: column-values returns user IDs while rows only carry names, so resolve via user-detail.
  const [userIdsByColumn, setUserIdsByColumn] = useState({});
  const [userNamesById, setUserNamesById] = useState({});
  const requestedUserIdsRef = useRef(new Set());

  useEffect(() => {
    if (!neededUserColumns.length) return undefined;
    let cancelled = false;
    // Re-fetch a column only when the scope or item count moved on since its last fetch.
    const fetchToken = `${organizationId ?? ""}:${projectId ?? ""}:${totalItems}`;
    const pendingColumns = new Set();
    neededUserColumns.forEach((column) => {
      if (fetchedUserColumnsRef.current[column] === fetchToken) return;
      fetchedUserColumnsRef.current[column] = fetchToken;
      pendingColumns.add(column);
      fetchInventoryColumnValues({
        column,
        ...(organizationId ? { organizationId: Number(organizationId) } : {}),
        ...(projectId ? { projectIds: [Number(projectId)] } : {}),
      })
        .then((raw) => {
          if (cancelled) return;
          pendingColumns.delete(column);
          const list = Array.isArray(raw) ? raw : raw?.values || [];
          const ids = list.map((item) => {
            const value = item !== null && typeof item === "object" ? item.value : item;
            return value === null || value === undefined ? BLANK_FILTER_VALUE : String(value);
          });
          const namedUsers = labelledOptions(list);
          if (Object.keys(namedUsers).length) setUserNamesById((prev) => ({ ...prev, ...namedUsers }));
          setUserIdsByColumn((prev) => ({ ...prev, [column]: [...new Set(ids)] }));
        })
        .catch(() => {
          if (cancelled) return;
          pendingColumns.delete(column);
          setUserIdsByColumn((prev) => ({ ...prev, [column]: [] }));
        });
    });
    return () => {
      cancelled = true;
      // A fetch cut off before it finished must be allowed to run again.
      pendingColumns.forEach((column) => {
        delete fetchedUserColumnsRef.current[column];
      });
    };
  }, [organizationId, projectId, totalItems, neededUserColumnsKey]);

  useEffect(() => {
    Object.values(userIdsByColumn)
      .flat()
      .filter((id) => isNumericId(id) && !requestedUserIdsRef.current.has(id))
      .forEach((id) => {
        requestedUserIdsRef.current.add(id);
        getUserInfoAPI(id)
          .then((response) => {
            const name = response?.data?.body?.value?.name;
            if (name) setUserNamesById((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }));
          })
          .catch(() => {});
      });
  }, [userIdsByColumn]);

  const userFilterOptionsByColumn = useMemo(() => {
    const byColumn = {};
    USER_ID_COLUMNS.forEach((column) => {
      const ids = userIdsByColumn[column] || [];
      byColumn[column] = [
        ...(ids.includes(BLANK_FILTER_VALUE) ? [{ value: BLANK_FILTER_VALUE, label: "" }] : []),
        ...ids
          .filter((id) => id !== BLANK_FILTER_VALUE && (userNamesById[id] || !isNumericId(id)))
          .map((id) => ({ value: id, label: userNamesById[id] ?? id }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    });
    return byColumn;
  }, [userIdsByColumn, userNamesById]);
  const sourceFilterOptions = useMemo(
    () => [
      { value: "MANUAL", label: "MANUAL" },
      { value: "UPLOAD", label: "UPLOAD" },
      { value: "API", label: "API" },
      { value: "SYSTEM", label: "SYSTEM" },
    ],
    []
  );
  const statusBadgeStyles = {
    AVAILABLE: { bg: "#DCF5E3", text: "#1E9E57" },
    ASSIGNED: { bg: "#DEE8FF", text: "#2563EB" },
    REASSIGNED: { bg: "#CCFBF1", text: "#0F766E" },
    IN_TRANSIT: { bg: "#FFF1D6", text: "#F2930D" },
    INSTALLED: { bg: "#DEE8FF", text: "#2563EB" },
    REMOVED: { bg: "#FEE2E2", text: "#B91C1C" },
    RETURNED: { bg: "#EDE9FE", text: "#6D28D9" },
    DAMAGED: { bg: "#FEE2E2", text: "#B91C1C" },
    QUARANTINED: { bg: "#FEF3C7", text: "#B45309" },
    LOST: { bg: "#E2E8F0", text: "#475569" },
    RETIRED: { bg: "#E2E8F0", text: "#475569" },
  };
  const getStatusBadgeStyle = (code) =>
    statusBadgeStyles[code] || { bg: "#E2E8F0", text: "#475569" };
  const sourceBadgeStyles = {
    MANUAL: { bg: "#E7F7F8", text: "#2295A0" },
    UPLOAD: { bg: "#E8F4FD", text: "#1D6DC2" },
    API: { bg: "#FFF1D6", text: "#F2930D" },
    SYSTEM: { bg: "#F0ECF9", text: "#6A41C8" },
  };
  const getSourceBadgeStyle = (value) =>
    sourceBadgeStyles[String(value ?? "").toUpperCase()] || { bg: "#E2E8F0", text: "#475569" };
  const statusBadgeIcons = {
    AVAILABLE: PackageCheck,
    ASSIGNED: UserCheck,
    REASSIGNED: Repeat2,
    IN_TRANSIT: Truck,
    INSTALLED: CircleCheck,
    REMOVED: XCircle,
    RETURNED: RotateCcw,
    DAMAGED: AlertTriangle,
    QUARANTINED: ShieldAlert,
    LOST: HelpCircle,
    RETIRED: Archive,
  };
  const sourceBadgeIcons = {
    MANUAL: Hand,
    UPLOAD: Upload,
    API: Webhook,
    SYSTEM: Cpu,
  };
  const mapRow = (item) => {
    const statusLabel =
      statusLabels[item.status] || formatStatusLabel(item.status) || EMPTY_VALUE;
    const meterTypeValue =
      item.meterType ?? item.metadata?.meterType ?? itemTypeLabels[item.itemType];
    const meterMakeValue = item.meterMake ?? item.metadata?.meterMake;
    const manufacturingYearValue =
      item.manufacturingYear ?? item.metadata?.manufacturingYear;
    const batchNumberValue = item.batchNo ?? item.metadata?.batchNo;
    const componentsValue = item.components ?? item.metadata?.components;
    const groupDetails = normalizeGroupDetails(item.groupAssignmentDetails);
    const groupName = groupDetails?.groupName || item.groupAssignment || item.group;
    return {
      raw: item,
      id: item.id ?? item.inventoryItemId ?? EMPTY_VALUE,
      meterNumber: item.identifier ?? item.meterNo ?? item.meterSerialNumber ?? item.meterNumber ?? item.meterBarCode ?? EMPTY_VALUE,
      identifier: item.identifier ?? item.meterNo ?? item.meterSerialNumber ?? item.meterNumber ?? item.meterBarCode ?? EMPTY_VALUE,
      meterBarcode: item.meterBarCode ?? item.identifier ?? EMPTY_VALUE,
      meterType:
        meterTypeValue ??
        formatStatusLabel(item.itemType) ??
        EMPTY_VALUE,
      meterMake: meterMakeValue ?? EMPTY_VALUE,
      manufacturingYear: manufacturingYearValue ?? EMPTY_VALUE,
      activationDate: formatDateValue(item.activationDate),
      batchNumber: batchNumberValue ?? EMPTY_VALUE,
      components: componentsValue ?? EMPTY_VALUE,
      apn: item.apn ?? EMPTY_VALUE,
      ipAddress: item.ipAddress ?? EMPTY_VALUE,
      installationStatus: item.installationStatus ?? EMPTY_VALUE,
      transferredFrom: item.transferredFrom ?? EMPTY_VALUE,
      transferredTo: item.transferredTo ?? EMPTY_VALUE,
      conditionNote: item.conditionNote ?? EMPTY_VALUE,
      source: item.source ?? EMPTY_VALUE,
      inventoryUploadId: item.inventoryUploadId ?? EMPTY_VALUE,
      createdBy: item.createdBy ?? EMPTY_VALUE,
      updatedAt: formatDateValue(item.updatedAt),
      storeLocation: item.location ?? item.storeLocation ?? item.storeName ?? EMPTY_VALUE,
      location: item.location ?? item.storeLocation ?? item.storeName ?? EMPTY_VALUE,
      latitude: toCoordinate(item.latitude, 90),
      longitude: toCoordinate(item.longitude, 180),
      group: groupName ?? EMPTY_VALUE,
      groupAssignment: groupName ?? EMPTY_VALUE,
      groupName: groupName || "",
      groupDetails,
      assignment: getAssignmentCode(item) === "ASSIGNED" ? t.inventoryAssigned : t.inventoryAvailable,
      assignmentCode: getAssignmentCode(item),
      assignedTo: item.assignedTo ?? "",
      assignedToName: item.assignedToName ?? "",
      statusCode: item.status || "",
      status: statusLabel,
      simNo: item.metadata?.simNo ?? item.simNo ?? EMPTY_VALUE,
      modemNo: item.metadata?.modemNo ?? item.modemNo ?? EMPTY_VALUE,
      installationDate: formatDateValue(item.installationDate ?? item.installedAt ?? ""),
      createdAt: formatDateValue(item.createdAt ?? ""),
    };
  };
  const openDetails = (row) => {
    setActiveRow(row);
    setEditingDetails(false);
    setDrawerOpen(true);
  };
  const handleDetailsSaved = () => {
    setEditingDetails(false);
    loadInventory();
  };
  const openHistory = (row) => {
    setDrawerOpen(false);
    setHistoryDrawerItem(row);
    setHistoryDrawerOpen(true);
  };
  const loadInventory = async (signal, requestId) => {
    const activeRequestId = requestId ?? ++loadRequestIdRef.current;
    // organizationId is required by the search API.
    if (!organizationId) {
      setRows([]);
      setTotalItems(0);
      setTotalPages(0);
      setLoadError(false);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchInventoryItems(buildQueryParams(), { signal });
    
      if (activeRequestId !== loadRequestIdRef.current) return;
      const normalized = normalizeResponse(data, pageSize);
      setRows((normalized.items || []).map(mapRow));
      setTotalItems(normalized.total ?? 0);
      setTotalPages(normalized.pages ?? 0);
      setLoadError(false);
    } catch (error) {
      if (
        error?.code === "ERR_CANCELED" ||
        error?.name === "CanceledError" ||
        error?.name === "AbortError"
      ) {
        return;
      }
      if (activeRequestId !== loadRequestIdRef.current) return;
     
      setRows([]);
      setTotalItems(0);
      setTotalPages(0);
       setLoadError(true);

  toast.error(
    apiErrorMessage(error, t.somethingwentwrong || t.noDataFound)
  );
} finally {
  setLoading(false);
}
  };

  const combinedColumnFilters = useMemo(() => {
    const combined = {};
    if (appliedSearch?.value) {
     
      if (appliedSearch.key === "meterType" || appliedSearch.key === "itemType") {
        const normalizedValue = appliedSearch.value.trim().toLowerCase();
        const matchedItemTypeCode = Object.entries(itemTypeLabels).find(([, label]) =>
          String(label ?? "").toLowerCase().includes(normalizedValue)
        )?.[0];
        if (matchedItemTypeCode) {
          combined.itemType = makeInFilter("itemType", [matchedItemTypeCode]);
        } else {
          combined[appliedSearch.key] = makeContainsFilter(appliedSearch.key, appliedSearch.value);
        }
      } else if (appliedSearch.key === "storeId") {
        const normalizedValue = appliedSearch.value.trim().toLowerCase();
        const matchedStoreIds = storeFilterOptions
          .filter((option) => String(option.label ?? "").toLowerCase().includes(normalizedValue))
          .map((option) => option.value);
        combined.storeId = matchedStoreIds.length
          ? makeInFilter("storeId", matchedStoreIds)
          : makeContainsFilter("storeId", appliedSearch.value);
      } else if (USER_ID_COLUMNS.includes(appliedSearch.key)) {
        const normalizedValue = appliedSearch.value.trim().toLowerCase();
        const matchedUserIds = (userFilterOptionsByColumn[appliedSearch.key] || [])
          .filter((option) => String(option.label ?? "").toLowerCase().includes(normalizedValue))
          .map((option) => option.value);
        combined[appliedSearch.key] = matchedUserIds.length
          ? makeInFilter(appliedSearch.key, matchedUserIds)
          : makeContainsFilter(appliedSearch.key, appliedSearch.value);
      } else {
        const apiColumn = columnKeyToApiColumn(appliedSearch.key);
        combined[apiColumn] = makeContainsFilter(apiColumn, appliedSearch.value);
      }
    }
  
    Object.assign(combined, columnFilters);
    return combined;
  }, [appliedSearch, columnFilters, storeFilterOptions, userFilterOptionsByColumn, t]);

  const serializedColumnFilters = useMemo(
    () => serializeColumnFilters(combinedColumnFilters),
    [combinedColumnFilters]
  );
  const serializedColumnFiltersKey = JSON.stringify(serializedColumnFilters);
  // The Export button names the filters going into the export (the serialized
  // ones, search included), the same way the task list's Export button counts them.
  const exportFilterColumns = useMemo(
    () => [
      ...new Set(
        serializedColumnFilters.map((filter) => columnMetaByKey.get(filter.column)?.label || filter.column)
      ),
    ],
    [serializedColumnFilters, columnMetaByKey]
  );
  const exportButtonLabel = !exportFilterColumns.length
    ? t.inventoryExport
    : `${t.inventoryExport} (${
        exportFilterColumns.length === 1
          ? t.exportOneFilter
          : t.exportManyFilters.replace("{count}", exportFilterColumns.length)
      })`;
  const exportButtonTooltip = exportFilterColumns.length
    ? t.inventoryExportFilteredTooltip.replace("{columns}", exportFilterColumns.join(", "))
    : t.inventoryExportTooltip;

  useEffect(() => {
  
    if (!hasHydratedInventoryFilters) return undefined;
    const abortController = new AbortController();
    const requestId = ++loadRequestIdRef.current;
    loadInventory(abortController.signal, requestId);
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasHydratedInventoryFilters,
    currentPage,
    pageSize,
    appliedSearch?.key,
    appliedSearch?.value,
    filterStoreId,
    filterAssignedTo,
    filterGroupAssignment,
    organizationId,
    projectId,
    serializedColumnFiltersKey,
  ]);

  const appliedFilterCount =
    (appliedSearch?.value ? 1 : 0) +
    (filterStoreId ? 1 : 0) +
    (filterAssignedTo ? 1 : 0) +
    (filterGroupAssignment ? 1 : 0) +
    countActiveColumnFilters(columnFilters);
  const hasActiveFilters = appliedFilterCount > 0;

  const handleResetToolbar = () => {
    setSearchFieldValues({});
    setAppliedSearch({ key: searchFieldKey, value: "" });
    setFilterStoreId("");
    setFilterAssignedTo("");
    setFilterGroupAssignment("");
    setColumnFilters({});
    setCurrentPage(0);
  };

  const startIndex = totalItems === 0 ? 0 : currentPage * pageSize + 1;
  const endIndex = totalItems === 0 ? 0 : Math.min(totalItems, (currentPage + 1) * pageSize);
  const tableColumnCount = visibleColumnKeys.length + 3;
  const filteredRows = rows;

  const selectedIdSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds]);
  const toggleRowSelection = (rowId) =>
    setSelectedRowIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  // Selection is limited to the current page and resets when pagination changes.
  const toggleSelectAllOnPage = () => {
    const pageIds = filteredRows.map((row) => row.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIdSet.has(id));
    setSelectedRowIds((prev) =>
      allSelected
        ? prev.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...prev, ...pageIds]))
    );
  };
  const allPageRowsSelected =
    filteredRows.length > 0 && filteredRows.every((row) => selectedIdSet.has(row.id));
  const selectedItems = useMemo(
    () =>
      rows
        .filter((row) => selectedIdSet.has(row.id))
        .map((row) => ({
          id: row.raw?.id ?? row.raw?.inventoryItemId ?? row.id,
          identifier: row.raw?.identifier ?? row.raw?.meterBarCode ?? row.meterNumber,
          storeId: row.raw?.storeId ?? row.raw?.store?.id ?? row.raw?.store?.storeId,
          organizationId: row.raw?.organizationId ?? row.raw?.organization?.organizationId ?? row.raw?.orgId,
          projectId: row.raw?.projectId ?? row.raw?.project?.id ?? row.raw?.project?.projectId,
          raw: row.raw,
        })),
    [rows, selectedIdSet]
  );
  const transferDisabled = !canUpdateInventory || selectedItems.some(isInventoryActionBlocked);
  const actionsDisabled = !canUpdateInventory || selectedItems.length === 0 || selectedItems.some((item) => !isInventoryReassignAllowed(item));
  const openTransferModal = (mode = "transfer") => {
    if (mode === "reassign" ? actionsDisabled : transferDisabled) {
      toast.warn(t.inventoryTransferNoSelection);
      return;
    }
    setTransferMode(mode);
    setTransferModalOpen(true);
  };

  const activeItem = activeRow?.raw || {};
  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return EMPTY_VALUE;
    return value;
  };
  
  const searchFields = useMemo(() => {
    const pinnedKey = "identifier";
    const pinned = normalizedColumns.find((col) => col.key === pinnedKey);
    const rest = normalizedColumns.filter((col) => col.key !== pinnedKey);
    return (pinned ? [pinned, ...rest] : normalizedColumns).map((col) => ({
      key: col.key,
      label: col.label,
    }));
  }, [normalizedColumns]);
  const activeSearchField = useMemo(() => {
    const found = searchFields.find((f) => f.key === searchFieldKey);
    return found || searchFields[0];
  }, [searchFields, searchFieldKey]);
  const activeSearchValue = searchFieldValues[activeSearchField?.key] ?? "";
  const applySearch = () => {
    const value = String(activeSearchValue ?? "").trim();
    setAppliedSearch({ key: activeSearchField.key, value });
    setCurrentPage(0);
  };
  const clearSearch = () => {
    setSearchFieldValues((prev) => ({ ...prev, [activeSearchField.key]: "" }));
    setAppliedSearch({ key: activeSearchField.key, value: "" });
    setCurrentPage(0);
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      applySearch();
    }, 300);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearchField.key, activeSearchValue]);

  useEffect(() => {
    if (showColumnFilters && !prevShowFiltersRef.current) {
      setJustRevealedFilters(true);
      const t = setTimeout(() => setJustRevealedFilters(false), 900);
      prevShowFiltersRef.current = showColumnFilters;
      return () => clearTimeout(t);
    }
    prevShowFiltersRef.current = showColumnFilters;
  }, [showColumnFilters]);

  useEffect(() => {
    if (!organizationId) return undefined;
    let cancelled = false;
    const params = {
      organizationId: Number(organizationId),
      ...(projectId ? { projectIds: [Number(projectId)] } : {}),
    };
    fetchInventoryColumns(params)
      .then((cols) => {
        if (cancelled) return;
        const list = Array.isArray(cols) ? cols : cols?.value || [];
        setInventoryColumns(list);
        setInventoryColumnsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setInventoryColumns([]);
        setInventoryColumnsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, projectId]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(INVENTORY_TABLE_LAYOUT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const savedOrder = Array.isArray(parsed?.colOrder) ? parsed.colOrder : [];
      if (savedOrder.length) setVisibleColumnKeys(savedOrder);
    } catch (error) {
      console.error("Error reading inventory table layout:", error);
    }
  }, []);

  useEffect(() => {
    const saved = readInventoryTableFilters();
    if (!saved) {
      setHasHydratedInventoryFilters(true);
      return;
    }
    try {
      const savedKey = String(saved?.searchFieldKey ?? "").trim();
      const savedValue = String(saved?.searchValue ?? "").trim();
      if (savedKey) {
        setSearchFieldKey(savedKey);
        setSearchFieldValues(savedValue ? { [savedKey]: savedValue } : {});
        setAppliedSearch({ key: savedKey, value: savedValue });
      }
      const savedColumnFilters = saved?.columnFilters;
      if (savedColumnFilters && typeof savedColumnFilters === "object") {
        setColumnFilters(
          Object.fromEntries(
            Object.entries(savedColumnFilters).filter(([, filter]) => isMeaningfulColumnFilter(filter))
          )
        );
      }
      if (saved?.filterStoreId) setFilterStoreId(String(saved.filterStoreId));
      if (saved?.filterAssignedTo) setFilterAssignedTo(String(saved.filterAssignedTo));
      if (saved?.filterGroupAssignment) setFilterGroupAssignment(String(saved.filterGroupAssignment));
    } catch (error) {
      console.error("Error restoring inventory table filters:", error);
    }
    setHasHydratedInventoryFilters(true);
  }, []);

  useEffect(() => {
    if (!hasHydratedInventoryFilters) return;
    try {
      const payload = {
        searchFieldKey,
        searchValue: appliedSearch?.value ?? "",
        columnFilters,
        filterStoreId,
        filterAssignedTo,
        filterGroupAssignment,
      };
      const serialized = JSON.stringify(payload);
      sessionStorage.setItem(INVENTORY_TABLE_FILTERS_KEY, serialized);
      localStorage.setItem(INVENTORY_TABLE_FILTERS_KEY, serialized);
    } catch (error) {
      console.error("Error saving inventory table filters:", error);
    }
  }, [
    searchFieldKey,
    appliedSearch,
    columnFilters,
    filterStoreId,
    filterAssignedTo,
    filterGroupAssignment,
    hasHydratedInventoryFilters,
  ]);

  useEffect(() => {
    // Wait for the real backend column list -- normalizedColumns falls back
    // to INVENTORY_DEFAULT_COLUMNS before this loads, so pruning against it
    // here would permanently drop any restored column that isn't in that
    // fallback list (e.g. one the user added via Manage Columns that only
    // exists in the real backend list), before the real list ever arrives.
    if (!inventoryColumnsLoaded || !normalizedColumns.length) return;
    setVisibleColumnKeys((prev) => {
      const availableKeys = normalizedColumns.map((col) => col.key);
      const availableSet = new Set(availableKeys);
      const filtered = (prev || []).filter((key) => availableSet.has(key));
      return filtered.length ? filtered : availableKeys;
    });
  }, [inventoryColumnsLoaded, normalizedColumns]);

  useEffect(() => {
    const persistTimer = window.setTimeout(() => {
      try {
        localStorage.setItem(
          INVENTORY_TABLE_LAYOUT_KEY,
          JSON.stringify({
            colOrder: visibleColumnKeys,
          })
        );
      } catch (error) {
        console.error("Error saving inventory table layout:", error);
      }
    }, 120);

    return () => window.clearTimeout(persistTimer);
  }, [visibleColumnKeys]);

  const setColumnFilterEntry = (key, filter) => {
    const isAddingNew =
      isMeaningfulColumnFilter(filter) && !isMeaningfulColumnFilter(columnFilters?.[key]);
    if (isAddingNew && countActiveColumnFilters(columnFilters) >= MAX_COLUMN_FILTERS) {
      console.warn(`Inventory: max ${MAX_COLUMN_FILTERS} column filters allowed.`);
      return;
    }
    setColumnFilters((prev) => {
      const next = { ...(prev || {}) };
      if (isMeaningfulColumnFilter(filter)) next[key] = filter;
      else delete next[key];
      return next;
    });
  };
  const clearColumnFilter = (key) => {
    setColumnFilters((prev) => {
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });
  };
  const renderFilterHeader = (label, key) => {
    const filterEntry = columnFilters?.[key];
    const isActive = isMeaningfulColumnFilter(filterEntry);
    return (
      <div className="inline-flex items-center justify-start gap-2 max-w-full">
        <span className="inline-flex items-center gap-1 min-w-0">
          <CommonTooltip text="Drag to reorder column" position="top" disableDefaultDescription className="shrink-0">
            <span
              draggable
              onDragStart={(event) => handleColumnDragStart(event, key)}
              onDragEnd={handleColumnDragEnd}
              className="flex items-center gap-0.5 shrink-0 cursor-grab active:cursor-grabbing"
            >
              <GripVertical size={11} className="text-slate-300 shrink-0" />
            </span>
          </CommonTooltip>
          <CommonTooltip text={label} position="top" disableDefaultDescription className="block truncate min-w-0">
            {label}
          </CommonTooltip>
          {SEAL_LIST_KEYS.has(key) && (
            <SealInfoIcon title={t.inventorySealNumbersInfoTitle} description={t.inventorySealNumbersInfo} />
          )}
        </span>
        {showColumnFilters && (
          <span className={justRevealedFilters ? "inline-flex shrink-0 animate-filter-pulse" : "inline-flex shrink-0"}>
            <HeaderFilterPopover
              active={isActive}
              tooltipText={`${t.filterByColumn} ${label}`}
            >
              {(closePopover) => (
                <ColumnValueFilterList
                  column={columnKeyToApiColumn(key)}
                  staticOptions={
                    key === "status"
                      ? statusFilterOptions
                      : key === "itemType"
                        ? itemTypeFilterOptions
                      : key === "source"
                        ? sourceFilterOptions
                        : key === "storeId"
                          ? storeFilterOptions
                          : userFilterOptionsByColumn[key]
                  }
                  staticLoading={
                    key === "storeId"
                      ? !storeValuesLoaded
                      : USER_ID_COLUMNS.includes(key)
                        ? userIdsByColumn[key] === undefined
                        : false
                  }
                  onOpen={
                    key === "storeId" || USER_ID_COLUMNS.includes(key)
                      ? () => markValueColumnOpened(key)
                      : undefined
                  }
                  selected={filterEntry?.op === "in" ? filterEntry.values : []}
                  containsValue={filterEntry?.op === "contains" ? filterEntry.value : ""}
                  onApply={(values) => {
                    setColumnFilterEntry(key, makeInFilter(key, values));
                    closePopover();
                  }}
                  onApplyContains={(value) => {
                    setColumnFilterEntry(key, makeContainsFilter(key, value));
                    closePopover();
                  }}
                  onClear={() => clearColumnFilter(key)}
                  searchPlaceholder={t.inventoryFilterSearchValues}
                  containsPlaceholder={t.inventoryFilterContainsPlaceholder}
                  applyLabel={t.inventoryFilterOk}
                  clearLabel={t.clear}
                  selectAllLabel={t.inventorySelectAll}
                  selectNoneLabel={t.inventorySelectNone}
                  noValuesLabel={t.inventoryFilterNoValues}
                  loadingLabel={t.inventoryFilterLoadingValues}
                  blanksLabel={t.inventoryFilterBlanks}
                  containsHelpText={t.inventoryFilterContainsHelp}
                />
              )}
            </HeaderFilterPopover>
          </span>
        )}
      </div>
    );
  };

  const handleColumnDragStart = (event, key) => {
    // draggable lives on the grip-handle span itself now, so only that
    // handle can ever start a drag -- no need to guard by event target.
    setDragColKey(key);
    setDragOverColKey(null);
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", key);
    }
  };

  const handleColumnDragEnd = () => {
    setDragColKey(null);
    setDragOverColKey(null);
  };

  const handleColumnDragOver = (event, key) => {
    if (!dragColKey || dragColKey === key) return;
    event.preventDefault();
    setDragOverColKey(key);
  };

  const handleColumnDrop = (event, key) => {
    event.preventDefault();
    const sourceKey = dragColKey || event?.dataTransfer?.getData("text/plain");
    if (!sourceKey || sourceKey === key) {
      setDragOverColKey(null);
      return;
    }
    setVisibleColumnKeys((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const fromIndex = current.indexOf(sourceKey);
      const toIndex = current.indexOf(key);
      if (fromIndex === -1 || toIndex === -1) return current;
      const next = [...current];
      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, sourceKey);
      return next;
    });
    setDragColKey(null);
    setDragOverColKey(null);
  };

  const getHeaderDragClass = (key) => {
    if (dragOverColKey === key && dragColKey && dragColKey !== key) return "bg-blue-100";
    if (dragColKey === key) return "opacity-40";
    return "";
  };

 
  const INVENTORY_COMPACT_COLUMN_WIDTHS_PX = {
    identifier: 150,
    meternumber: 150,
    metertype: 140,
    location: 160,
    storelocation: 160,
    groupassignment: 140,
    group: 140,
    assignedto: 160,
    assignment: 160,
    status: 130,
    simno: 140,
    modemno: 140,
    installationdate: 170,
    createdat: 160,
    sealnumbers: 170,
  };
  const INVENTORY_DATA_COLUMN_MIN_WIDTH_PX = 110;
  const INVENTORY_DATA_COLUMN_MAX_WIDTH_PX = 200;

  const getInventoryColumnWidth = (key, label) => {
    const normalizedKey = String(key ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
    if (INVENTORY_COMPACT_COLUMN_WIDTHS_PX[normalizedKey]) {
      return INVENTORY_COMPACT_COLUMN_WIDTHS_PX[normalizedKey];
    }
    const labelLength = String(label ?? key ?? "").trim().length;
    const estimatedWidth = labelLength * 7 + 38;
    return Math.max(
      INVENTORY_DATA_COLUMN_MIN_WIDTH_PX,
      Math.min(INVENTORY_DATA_COLUMN_MAX_WIDTH_PX, estimatedWidth)
    );
  };

  // Sized from the currently visible columns instead of a flat hardcoded
  // guess (mirrors TaskListView's tableMinWidthPx) -- a hardcoded min-width
  // forces the table to stay that wide even when fewer columns are shown,
  // stretching the remaining ones out with extra gap.
  const CHECKBOX_COL_WIDTH_PX = 60;
  const NO_COL_WIDTH_PX = 60;
  const ACTION_COL_WIDTH_PX = 108;
  const tableMinWidthPx = useMemo(() => {
    const visibleDataColumnsWidth = normalizedColumns
      .filter((col) => visibleColumnKeySet.has(col.key))
      .reduce((sum, col) => sum + getInventoryColumnWidth(col.key, col.label), 0);
    return CHECKBOX_COL_WIDTH_PX + NO_COL_WIDTH_PX + visibleDataColumnsWidth + ACTION_COL_WIDTH_PX;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedColumns, visibleColumnKeySet]);

  const getColumnValue = (row, key, metaValue) => {
    const raw = row?.raw || {};
    const valueLookup = {
      storeId: raw.storeName ?? EMPTY_VALUE,
      itemType: raw.itemType ? itemTypeLabels[raw.itemType] ?? formatStatusLabel(raw.itemType) : undefined,
      updatedAt: row.updatedAt,
      components: raw.components ?? raw.metadata?.components ?? raw.metadata?.meterComponents,
      // Column keys are snake_case but the item fields are camelCase.
      transfer_from: raw.transferFrom ?? raw.transfer_from,
      transfer_to: raw.transferTo ?? raw.transfer_to,
      account_no: raw.account_no,
      meterType: row.meterType,
      simNo: row.simNo,
      modemNo: row.modemNo,
      installationDate: row.installationDate,
      createdAt: row.createdAt,
      // Seals fitted on an installed meter, and the range a SEAL item covers.
      sealNumbers: sealNumberList(raw.sealNumbers ?? raw.seal_numbers).join(", ") || undefined,
      seal_numbers: sealNumberList(raw.sealNumbers ?? raw.seal_numbers).join(", ") || undefined,
      sealRange: formatSealRange(raw) || undefined,
      sealCount: raw.sealCount ?? raw.totalSealCount ?? undefined,
    };
    return valueLookup[key] ?? metaValue ?? row?.[key] ?? EMPTY_VALUE;
  };

  // Text-only version of each table cell, so the details panel matches the table.
  const getDetailValue = (row, key) => {
    const raw = row?.raw || {};
    if (key === "identifier" || key === "meterNumber") return row.meterNumber;
    if (key === "location" || key === "storeLocation") return row.storeLocation;
    if (key === "group" || key === "groupAssignment") return row.groupName || EMPTY_VALUE;
    if (key === "assignedTo") return row.assignedToName || row.assignedTo || row.assignment || EMPTY_VALUE;
    if (key === "assignment" || key === "status" || key === "source") return row[key];
    const meta = columnMetaByKey.get(key);
    const metaValue = meta?.group === "metadata" ? raw?.metadata?.[key] : raw?.[key];
    return getColumnValue(row, key, metaValue);
  };

  const renderDynamicCell = (row, header) => {
    const key = header?.value;
    const width = getInventoryColumnWidth(key, header?.label);
    const meta = columnMetaByKey.get(key);
    const raw = row?.raw || {};
    const metaValue = meta?.group === "metadata" ? raw?.metadata?.[key] : raw?.[key];
    const baseClass = "px-2 py-2.5 text-xs font-normal text-center truncate overflow-hidden whitespace-nowrap";
    const cellText = (text) => <InventoryCellText text={text} />;

    // A SEAL row has no fitted seals of its own, so its Seal Numbers cell shows how
    // much of the range is left, with the fitted seals in the tooltip.
    if ((key === "sealNumbers" || key === "seal_numbers") && isSealType(raw.itemType)) {
      const stock = sealStockSummary(raw);
      if (!stock || stock.available === null) {
        return (
          <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={baseClass}>
            {EMPTY_VALUE}
          </td>
        );
      }
      const remainingText =
        stock.total !== null
          ? t.inventorySealRemainingOfTotal
              .replace("{available}", stock.available.toLocaleString())
              .replace("{total}", stock.total.toLocaleString())
          : `${t.inventorySealRemaining}: ${stock.available.toLocaleString()}`;
      const tooltipText = stock.usedSealNumbers.length
        ? `${t.inventorySealsFitted} (${stock.used}): ${stock.usedSealNumbers.join(", ")}`
        : t.inventorySealNoneFitted;
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={baseClass}>
          <CommonTooltip
            text={tooltipText}
            position="top"
            disableDefaultDescription
            className="max-w-full"
            tooltipClassName="w-max max-w-[min(24rem,calc(100vw-1.5rem))] whitespace-normal break-words"
          >
            <span
              className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100 max-w-full truncate"
              data-testid="inventory-seal-remaining"
            >
              {remainingText}
            </span>
          </CommonTooltip>
        </td>
      );
    }

    // Seals fitted on an installed meter: the first one as a chip, the rest as "+N",
    // and the full list with an explanation in the tooltip.
    if (key === "sealNumbers" || key === "seal_numbers") {
      const seals = sealNumberList(raw.sealNumbers ?? raw.seal_numbers);
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className="px-2 py-2.5 text-xs text-center overflow-hidden">
          {seals.length ? (
            <CommonTooltip
              text={t.inventorySealNumbersCellTooltip.replace("{count}", seals.length)}
              description={`${seals.join(", ")}\n${t.inventorySealNumbersInfo}`}
              position="top"
              disableDefaultDescription
              className="inline-flex max-w-full"
              tooltipClassName="max-w-[min(22rem,calc(100vw-1.5rem))] whitespace-pre-line break-words"
            >
              <SealChips seals={seals} max={1} size="sm" className="justify-center flex-nowrap" />
            </CommonTooltip>
          ) : (
            EMPTY_VALUE
          )}
        </td>
      );
    }

    if (key === "identifier" || key === "meterNumber") {
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={`${baseClass} whitespace-nowrap`}>
          {cellText(row.meterNumber)}
        </td>
      );
    }

    if (key === "location" || key === "storeLocation") {
      const hasCoords =
        row.latitude !== null &&
        row.longitude !== null &&
        !(row.latitude === 0 && row.longitude === 0);
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={`${baseClass} whitespace-nowrap`}>
          <span className="inline-flex items-center gap-1 max-w-full">
            <span className="min-w-0 flex-1">{cellText(displayValue(row.storeLocation))}</span>
            {hasCoords && (
              <StoreLocationButton
                onOpen={() =>
                  setStoreLocationView({
                    latitude: row.latitude,
                    longitude: row.longitude,
                    label: row.storeLocation,
                  })
                }
                tooltipText={t.inventoryCurrentLocation}
              />
            )}
          </span>
        </td>
      );
    }

    if (key === "group" || key === "groupAssignment") {
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className="px-2 py-2.5 text-xs text-center overflow-hidden">
          <InventoryGroupCell
            details={row.groupDetails}
            fallbackName={row.groupName}
            membersLabel={t.inventoryGroupMembers}
            rtl={isRtl}
            renderName={cellText}
          />
        </td>
      );
    }

    if (key === "assignedTo") {
      const assignedValue = row.assignedToName || row.assignedTo || row.assignment || EMPTY_VALUE;
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={`${baseClass} whitespace-nowrap`}>
          {cellText(displayValue(assignedValue))}
        </td>
      );
    }

    if (key === "assignment") {
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={baseClass}>
          <span
            className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold max-w-full"
            style={{
              backgroundColor: getStatusBadgeStyle(row.assignmentCode).bg,
              color: getStatusBadgeStyle(row.assignmentCode).text,
            }}
          >
            {cellText(row.assignment)}
          </span>
        </td>
      );
    }

    // Missing status/source shows a plain placeholder rather than an empty colored badge.
    if ((key === "status" || key === "source") && displayValue(row[key]) === EMPTY_VALUE) {
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={baseClass}>
          {EMPTY_VALUE}
        </td>
      );
    }

    if (key === "status") {
      const StatusIcon = statusBadgeIcons[row.statusCode];
      const statusTooltip = statusTooltips[row.statusCode];
      const statusBadge = (
        <span
          className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold rounded-md border border-current/15 uppercase tracking-[0.08em] max-w-full"
          style={{
            backgroundColor: getStatusBadgeStyle(row.statusCode).bg,
            color: getStatusBadgeStyle(row.statusCode).text,
          }}
        >
          {StatusIcon && <StatusIcon className="shrink-0" size={14} />}
          {cellText(row.status)}
        </span>
      );
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={baseClass}>
          {statusTooltip ? (
            <CommonTooltip
              text={statusTooltip}
              position="top"
              disableDefaultDescription
              className="max-w-full"
              // Fit the bubble to the text (no empty space on the right) and keep it inside small screens.
              tooltipClassName="w-max max-w-[min(12rem,calc(100vw-1.5rem))] [text-wrap:balance]"
              fitContent
              clampToViewport
              hoverOnly
            >
              {statusBadge}
            </CommonTooltip>
          ) : (
            statusBadge
          )}
        </td>
      );
    }

    if (key === "source") {
      const SourceIcon = sourceBadgeIcons[String(row.source ?? "").toUpperCase()];
      return (
        <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={baseClass}>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-current/15 uppercase tracking-[0.08em] max-w-full"
            style={{
              backgroundColor: getSourceBadgeStyle(row.source).bg,
              color: getSourceBadgeStyle(row.source).text,
            }}
          >
            {SourceIcon && <SourceIcon className="shrink-0" size={14} />}
            {cellText(row.source)}
          </span>
        </td>
      );
    }

    return (
      <td key={key} style={{ width, minWidth: width, maxWidth: width }} className={`${baseClass} whitespace-nowrap`}>
        {cellText(displayValue(getColumnValue(row, key, metaValue)))}
      </td>
    );
  };

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className={`min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex flex-col overflow-y-auto sm:overflow-hidden ${
        theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
      }`}
    >
      <div
        className={`shrink-0 flex flex-col gap-3 mx-2 mt-2 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:mx-4 sm:mt-3 sm:gap-4 sm:px-5 sm:py-4 ${
          theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
        }`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1
              className={`text-base font-bold text-slate-800 leading-tight sm:text-lg ${
                theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
              }`}
            >
              {t.meterInventoryTitle}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {t.meterInventorySubtitle}
            </p>
          </div>

          <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:justify-start sm:ms-auto">
            <CommonButton
              onClick={() => setExportModalOpen(true)}
              theme={theme}
              variant="outline"
              disabled={!organizationId}
              tooltipText={exportButtonTooltip}
              tooltipPosition="bottom"
              wrapperClassName="flex-1 min-w-[140px] flex sm:inline-block sm:flex-initial"
              className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
              data-testid="inventory-export-button"
            >
              <Download size={16} className="shrink-0" />
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">{exportButtonLabel}</span>
            </CommonButton>
            {/* Configuration is super-admin only, so other roles don't see the button at all. */}
            {isSuperAdmin() && (
            <CommonButton
              onClick={() => navigate("/inventory-management/configuration")}
              theme={theme}
              variant="outline"
              wrapperClassName="flex-1 min-w-[140px] flex sm:inline-block sm:flex-initial"
              className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
              data-testid="inventory-configuration-button"
            >
              <SlidersHorizontal size={16} className="shrink-0" />
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">
                {t.configuration || "Configuration"}
              </span>
            </CommonButton>
            )}
            <CommonButton
              theme={theme}
              variant="primary"
              disabled={transferDisabled}
              onClick={() => openTransferModal()}
              wrapperClassName="flex-1 min-w-[140px] flex sm:inline-block sm:flex-initial"
              className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
              data-testid="inventory-transfer-button"
            >
              <ArrowLeftRight size={16} className="shrink-0" />
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">{t.transferMeter}</span>
            </CommonButton>
            {/* Store management is super-admin only, so other roles don't see the button at all. */}
            {canCreateStore && (
            <CommonButton
              onClick={() => navigate("/inventory-management/stores")}
              theme={theme}
              variant="primary"
              tooltipText={t.storeManagementTooltip || t.inventoryCreateStoreTooltip}
              tooltipPosition="bottom"
              wrapperClassName="flex-1 min-w-[140px] flex sm:inline-block sm:flex-initial"
              className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
              data-testid="inventory-create-store-button"
            >
              <Plus size={16} className="shrink-0" />
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">
                {t.storeManagement || "Store Management"}
              </span>
            </CommonButton>
            )}
            <CommonButton
              onClick={() => navigate("/inventory-management/add")}
              theme={theme}
              variant="primary"
              disabled={!canWriteInventory}
              tooltipText={canWriteInventory ? t.inventoryAddTooltip : t.inventoryNoAccessTooltip}
              tooltipPosition="bottom"


              wrapperClassName="flex-1 min-w-[140px] flex sm:inline-block sm:flex-initial"
              className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
              data-testid="inventory-add-button"
            >
              <Plus size={16} className="shrink-0" />
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">
                {t.addInventory}
              </span>
            </CommonButton>
          </div>
        </div>

        <div
          className={`flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 ${
            theme === "dark"
              ? "dark-mode"
              : theme === "high-contrast"
                ? "high-contrast"
                : ""
          }`}
        >
          <div className="flex w-full min-w-0 items-center justify-between gap-1.5 sm:gap-2 sm:contents">
            <div className="hidden sm:flex h-9 px-3 rounded-lg border border-slate-200 items-center gap-1.5 text-sm shrink-0 text-slate-700">
              <span>
                {totalItems} {t.items}
              </span>
            </div>
            {/* Desktop-only: Filters sits next to the count, before the search
                box. On mobile it stays in its original spot in the 3-button
                grid below (see the sm:hidden twin there). */}
            <button
              type="button"
              onClick={() => setShowColumnFilters((prev) => !prev)}
              className="hidden sm:flex h-9 w-auto px-3 rounded-lg border text-sm font-medium items-center justify-center gap-2 bg-white shrink-0"
              style={{
                borderColor: showColumnFilters ? "#93C5FD" : "#E2E8F0",
                color: showColumnFilters ? "#1D4ED8" : "#334155",
              }}
            >
              <Filter size={13} />
              <span className="truncate">{t.filters}</span>
            </button>
          </div>

          <div className="flex h-9 w-full sm:flex-1 sm:min-w-[260px] sm:max-w-[360px]">
            <SearchFieldPicker
              options={searchFields.map((field) => ({
                value: field.key,
                label: field.label,
              }))}
              activeValue={searchFieldKey}
              onSelect={(value) => {
              
                setSearchFieldKey(value);
                setSearchFieldValues({});
                setAppliedSearch({ key: value, value: "" });
                setCurrentPage(0);
              }}
              searchPlaceholder={t.findAField}
              isRtl={isRtl}
            />
            <div className="relative flex-1 min-w-0">
              <Search
                size={15}
                className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${
                  isRtl ? "right-3" : "left-3"
                }`}
              />
              <input
                id="inventory-search"
                type="text"
                autoComplete="off"
                value={activeSearchValue}
                onChange={(e) =>
                  setSearchFieldValues((prev) => ({
                    ...prev,
                    [activeSearchField.key]: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") applySearch();
                }}
                placeholder={`${t.search} ${activeSearchField?.label ?? ""}...`}
                className={`w-full h-9 text-sm border border-slate-200 outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${
                  isRtl
                    ? "pr-9 pl-3 border-r-0 rounded-l-lg"
                    : "pl-9 pr-3 border-l-0 rounded-r-lg"
                }`}
              />
              {activeSearchValue && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className={`absolute top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 ${
                    isRtl ? "left-2" : "right-2"
                  }`}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1.5 w-full sm:w-auto sm:flex sm:items-center sm:justify-end sm:gap-2 sm:ms-auto">
            {/* Mobile only -- the desktop copy lives next to the item count,
                before the search box. */}
            <CommonTooltip text={t.inventoryFiltersTooltip} position="bottom" disableDefaultDescription className="w-full sm:w-auto">
            <button
              type="button"
              onClick={() => {
                setShowColumnFilters((prev) => !prev);
              }}
              data-testid="inventory-filters-button"
              className="h-9 sm:h-8 w-full sm:w-auto px-2.5 sm:px-3 rounded-lg border text-xs sm:text-sm font-medium flex items-center justify-center gap-1.5 sm:gap-2 bg-white sm:hidden"
              style={{
                borderColor: showColumnFilters ? "#93C5FD" : "#E2E8F0",
                color: showColumnFilters ? "#1D4ED8" : "#334155",
              }}
            >
              <Filter size={13} />{" "}
              <span className="truncate">{t.filters}</span>
            </button>
            </CommonTooltip>
            <CommonTooltip text={t.inventoryResetTooltip} position="bottom" disableDefaultDescription className="w-full sm:w-auto">
            <button
              type="button"
              className="relative h-9 w-full sm:w-auto px-2.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 flex items-center justify-center gap-1.5 disabled:opacity-40 hover:bg-slate-50"
              disabled={!hasActiveFilters}
              onClick={handleResetToolbar}
              data-testid="inventory-reset-button"
            >
              <RotateCcw size={13} />{" "}
              <span className="truncate hidden md:inline">
                {t.reset}
              </span>
              {hasActiveFilters && appliedFilterCount > 0 && (
                <span
                  className={`absolute -top-2 ${isRtl ? "-left-2" : "-right-2"} grid min-w-[20px] h-5 px-1 place-items-center rounded-full bg-[#ff3b30] text-white text-[11px] font-semibold leading-[20px] tabular-nums`}
                  aria-label={`${appliedFilterCount} applied filters`}
                >
                  {appliedFilterCount > 99 ? "99+" : appliedFilterCount}
                </span>
              )}
            </button>
            </CommonTooltip>

            <div ref={columnsButtonRef} className="relative h-9 sm:h-8 w-full sm:w-auto">
              <CommonTooltip
                text={t.columnsTooltip || t.columns}
                position="bottom"
                disableDefaultDescription
                className="h-full w-full"
              >
              <button
                type="button"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => setShowColumns((prev) => !prev)}
                className="h-9 w-full sm:w-auto px-2 sm:px-2.5 rounded-lg border text-xs font-medium flex items-center justify-center gap-1"
                style={{
                  borderColor: showColumns ? "#93C5FD" : "#E2E8F0",
                  background: showColumns ? "#EFF6FF" : "white",
                  color: showColumns ? "#1D4ED8" : "#475569",
                }}
                aria-expanded={showColumns}
                aria-label={t.columnsTooltip || t.columns}
              >
                <Columns3 size={13} />
                <span className="hidden md:inline">
                  {t.columns}
                </span>
                <span className="hidden sm:inline-flex h-5 min-w-[30px] items-center justify-center rounded-full bg-slate-100 px-1 text-[11px] leading-none tabular-nums text-slate-500">
                  {visibleColumns}/{totalColumns}
                </span>
              </button>
              </CommonTooltip>
              <ColumnsCustomizeModal
                open={showColumns}
                onClose={() => setShowColumns(false)}
                tableHeaders={tableHeaders}
                visibleColumnKeys={visibleColumnKeys}
                onApply={setVisibleColumnKeys}
                langMode={langMode}
                anchorRef={columnsButtonRef}
              />
            </div>
          </div>
        </div>

      </div>

      {selectedItems.length > 0 && (
        <div className={`mx-2 mt-3 flex min-w-0 flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 sm:mx-4 ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`} data-testid="inventory-selection-toolbar">
          <span className="min-w-0 flex-1 text-sm font-medium text-[#236DB4]">
            {t.inventoryTransferSelectedCount.replace("{count}", selectedItems.length)}
          </span>
          <div className="flex max-w-full flex-wrap items-center gap-2">
            <CommonButton theme={theme} variant="outline" disabled={actionsDisabled} onClick={() => openTransferModal("reassign")} data-testid="inventory-reassign-button">
              <RotateCcw size={16} /> {t.inventoryReassign}
            </CommonButton>
            <CommonButton theme={theme} variant="outline" disabled tooltipText={t.inventoryDeleteUnavailable} data-testid="inventory-delete-button">
              <Trash2 size={16} /> {t.delete || "Delete"}
            </CommonButton>
            <CommonButton theme={theme} variant="outline" disabled tooltipText={t.inventoryDeleteUnavailable} data-testid="inventory-delete-all-button">
              <Trash2 size={16} /> {t.deleteAllRecord}
            </CommonButton>
            <ModalCloseButton onClick={() => setSelectedRowIds([])} ariaLabel={t.cancel || "Clear selection"} />
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 w-full flex flex-col">
        <div
          className={`mx-2 mb-2 mt-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 ${
            theme === "dark"
              ? "dark-mode border"
              : theme === "high-contrast"
                ? "high-contrast"
                : ""
          }`}
        >
           <div className="relative z-20 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5">
            <span className="text-xs text-slate-500">
              {t.showing} {startIndex}-{endIndex} {t.of} {totalItems}
            </span>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="text-xs text-slate-500">
                {t.rowsPerPage}
              </span>
              <PageSizeSelector
                value={pageSize}
                onChange={(value) => {
                  setCurrentPage(0);
                  setPageSize(value);
                }}
                height="32px"
                fontSize="12px"
              />
              <span className="pager-nav flex items-center gap-1">
                <CommonTooltip text={t.previousPage} position="top" disableDefaultDescription>
                  <PagerArrowButton
                    direction="prev"
                    isRtl={isRtl}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                    disabled={currentPage === 0}
                  />
                </CommonTooltip>
                <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                  {Math.min(currentPage + 1, Math.max(totalPages, 1))} / {Math.max(totalPages, 1)}
                </span>
                <CommonTooltip text={t.nextPage} position="top" disableDefaultDescription>
                  <PagerArrowButton
                    direction="next"
                    isRtl={isRtl}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, Math.max(totalPages - 1, 0)))
                    }
                    disabled={currentPage >= Math.max(totalPages - 1, 0)}
                  />
                </CommonTooltip>
              </span>
            </div>
          </div>

          {loading && <Spinner />}

          <TableScrollShell
            theme={theme}
            fill
            className="w-full flex-1 min-h-0"
            innerClassName="task-list-scrollbar"
          >
            <table
              className={`table-fixed w-full border-collapse text-xs text-center text-gray-500 relative z-0 ${
                theme === "dark"
                  ? "dark-mode dark:text-gray-400"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
              }`}
              style={{ minWidth: `${tableMinWidthPx}px` }}
              dir={isRtl ? "rtl" : "ltr"}
            >
              <thead
                className={`sticky top-0 z-20 border-b text-xs text-slate-700 ${
                  theme === "dark"
                    ? "bg-gray-800 text-gray-400 border-gray-700"
                    : theme === "high-contrast"
                      ? "high-contrast bg-gray-100"
                      : "border-slate-200 bg-slate-50"
                }`}
              >
                <tr>
                  <th className="w-[60px] min-w-[60px] max-w-[60px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-400 text-center bg-slate-50">
                    <input
                      type="checkbox"
                      checked={allPageRowsSelected}
                      onChange={toggleSelectAllOnPage}
                      aria-label={t.inventorySelectAll}
                      data-testid="inventory-select-all-checkbox"
                      className="w-4 h-4 accent-blue-500"
                    />
                  </th>
                  <th className="w-[60px] min-w-[60px] max-w-[60px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-400 tracking-[0.04em] text-center bg-slate-50">
                    {t.inventoryRowNumber}
                  </th>
                  <th className="w-[108px] min-w-[108px] max-w-[108px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-500 tracking-[0.04em] text-center bg-slate-50">
                    {t.inventoryAction}
                  </th>
                  {orderedVisibleHeaders
                    .map((header) => {
                      const width = getInventoryColumnWidth(header.value, header.label);
                      return (
                        <th
                          key={header.value}
                          onDragOver={(event) => handleColumnDragOver(event, header.value)}
                          onDrop={(event) => handleColumnDrop(event, header.value)}
                          style={{ width, minWidth: width, maxWidth: width }}
                          className={`relative px-2 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 text-center bg-slate-50 ${getHeaderDragClass(
                            header.value
                          )}`}
                        >
                          {renderFilterHeader(header.label, header.value)}
                        </th>
                      );
                    })}
                </tr>
              </thead>
              <tbody className="text-xs">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={tableColumnCount}
                      className="py-16 h-[200px] align-middle"
                    >
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-blue-50 text-blue-600">
                          <Inbox size={22} />
                        </div>
                        <div className="text-sm font-semibold text-slate-800">
                          {!organizationId
                            ? t.selectOrgWarning
                            : loadError
                            ? t.inventoryLoadError
                            : hasActiveFilters
                              ? t.noRecordsMatchSearch
                              : t.noDataFound}
                        </div>
                        {organizationId && !loadError && (
                          <div className="text-xs mt-1 max-w-xs text-slate-400">
                            {hasActiveFilters ? t.tryAdjustingFilters : t.getStartedUploadingSheet}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, index) => (
                    <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      <td className="px-3 sm:px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIdSet.has(row.id)}
                          onChange={() => toggleRowSelection(row.id)}
                          aria-label={`${t.inventoryMeterNumber} ${row.meterNumber}`}
                          data-testid="inventory-row-checkbox"
                          className="w-4 h-4 accent-blue-500"
                        />
                      </td>
                      <td className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-2.5 text-xs text-[#70788f] whitespace-nowrap font-mono text-center">
                        {currentPage * pageSize + index + 1}
                      </td>
                      <td className="px-0 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          <CommonTooltip text={t.inventoryViewDetailsAria} position="top" disableDefaultDescription>
                            <ActionIconButton
                              onClick={() => openDetails(row)}
                              tone="blue"
                              ariaLabel={t.inventoryViewDetailsAria}
                              className="!h-7 !w-7 !rounded-md"
                            >
                              <Eye size={14} />
                            </ActionIconButton>
                          </CommonTooltip>
                          <CommonTooltip text={t.inventoryViewHistoryAria} position="top" disableDefaultDescription>
                            <ActionIconButton
                              onClick={() => openHistory(row)}
                              tone="blue"
                              ariaLabel={t.inventoryViewHistoryAria}
                              className="!h-7 !w-7 !rounded-md"
                            >
                              <History size={14} />
                            </ActionIconButton>
                          </CommonTooltip>
                        </div>
                      </td>
                      {orderedVisibleHeaders.map((header) => renderDynamicCell(row, header))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableScrollShell>
        </div>
      </div>

      <InventoryTransferModal
        mode={transferMode}
        open={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        selectedItems={selectedItems}
        onTransferred={(remainingIds = [], { reload = true } = {}) => {
          setSelectedRowIds(remainingIds);
          if (reload) loadInventory();
        }}
      />

      {storeLocationView && (
        <StoreLocationModal
          location={storeLocationView}
          onClose={() => setStoreLocationView(null)}
          t={t}
          isRtl={isRtl}
        />
      )}

      <InventoryExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        getExportPayload={buildExportPayload}
      />
      <InventoryHistoryDrawer
        open={historyDrawerOpen}
        item={historyDrawerItem}
        onClose={() => setHistoryDrawerOpen(false)}
      />

      {drawerOpen && (
        <div className="fixed inset-0 z-[1200]">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setDrawerOpen(false)}
            aria-label={t.inventoryClosePanel}
          />
          <aside
            className={`absolute top-0 ${
              isRtl ? "left-0" : "right-0"
            } h-[100dvh] w-[92vw] max-w-[520px] sm:w-[520px] sm:max-w-[520px] lg:w-[560px] lg:max-w-[560px] bg-white shadow-2xl border-l border-slate-200 flex flex-col`}
            dir={isRtl ? "rtl" : "ltr"}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  {t.inventoryDetailsTitle}
                </h2>
                <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                  <span>{activeRow?.meterNumber ?? EMPTY_VALUE}</span>
                </div>
              </div>
              <ModalCloseButton
                onClick={() => setDrawerOpen(false)}
                className="shrink-0"
                ariaLabel={t.inventoryClosePanel}
              />
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                      {t.inventoryDetailsSection}
                    </div>
                    {canUpdateInventory && !editingDetails && (
                      <button
                        type="button"
                        onClick={() => setEditingDetails(true)}
                        data-testid="inventory-edit-button"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#CFE3FB] bg-[#E8F1FB] px-3 py-1.5 text-sm font-medium text-[#236DB4] transition-colors hover:bg-[#D6E7F9] focus:outline-none focus:ring-2 focus:ring-[#236DB4]/30"
                      >
                        <Pencil size={14} aria-hidden="true" />
                        {t.inventoryEditItem}
                      </button>
                    )}
                  </div>

                  {editingDetails && (
                    <InventoryItemEditForm
                      key={activeRow?.id}
                      item={activeItem}
                      onCancel={() => setEditingDetails(false)}
                      onSaved={handleDetailsSaved}
                    />
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      ...normalizedColumns.map((col) => ({
                        key: col.key,
                        label: col.label,
                        value: getDetailValue(activeRow || {}, col.key),
                        icon: detailIconFor(col.key),
                      })),
                      // Fields not offered as table columns; shown only when the item has a value.
                      ...[
                        { key: "meterSerialNumber", label: t.inventoryMeterSerialNumber, value: activeItem.meterSerialNumber || activeItem.serialNumber },
                        { key: "manufacturingYear", label: t.inventoryManufacturingYear, value: activeItem.manufacturingYear || activeItem.metadata?.manufacturingYear },
                        { key: "batchNo", label: t.inventoryBatchNumber, value: activeItem.batchNo || activeItem.metadata?.batchNo },
                        { key: "meterMake", label: t.inventoryMeterMake, value: activeItem.meterMake || activeItem.metadata?.meterMake },
                        { key: "activationDate", label: t.inventoryActivationDate, value: activeItem.activationDate && formatDateValue(activeItem.activationDate) },
                        { key: "apn", label: t.inventoryApn, value: activeItem.apn },
                        { key: "ipAddress", label: t.inventoryIpAddress, value: activeItem.ipAddress },
                        { key: "installationStatus", label: t.inventoryInstallationStatus, value: activeItem.installationStatus },
                        { key: "inventoryUploadId", label: t.inventoryUploadId, value: activeItem.inventoryUploadId },
                        { key: "balance", label: t.inventoryBalance, value: activeItem.balance },
                        { key: "op", label: t.inventoryOp, value: activeItem.op },
                        { key: "simDetailUpdatedAt", label: t.inventorySimDetailUpdatedAt, value: activeItem.simDetailUpdatedAt && formatDateValue(activeItem.simDetailUpdatedAt) },
                        { key: "deletedAt", label: t.inventoryDeletedAt, value: activeItem.deletedAt && formatDateValue(activeItem.deletedAt) },
                        { key: "deletedBy", label: t.inventoryDeletedBy, value: activeItem.deletedBy },
                        { key: "deleteReason", label: t.inventoryDeleteReason, value: activeItem.deleteReason },
                        // Seal range stock is not offered by the columns API, so SEAL items add it here.
                        ...(isSealType(activeItem.itemType)
                          ? (() => {
                              const stock = sealStockSummary(activeItem);
                              return [
                                { key: "sealRange", label: t.inventorySealRange, value: formatSealRange(activeItem) },
                                { key: "totalSealCount", label: t.inventorySealTotal, value: stock?.total?.toLocaleString() },
                                { key: "usedSealCount", label: t.inventorySealFittedCount, value: stock?.used?.toLocaleString() },
                                { key: "availableSealCount", label: t.inventorySealRemaining, value: stock?.available?.toLocaleString() },
                                { key: "usedSealNumbers", label: t.inventorySealsFitted, value: stock?.usedSealNumbers ?? [] },
                              ];
                            })()
                          : []),
                      ]
                        .filter((field) => !columnMetaByKey.has(field.key))
                        .filter((field) => field.value !== undefined && field.value !== null && field.value !== "")
                        .map((field) => ({ ...field, icon: detailIconFor(field.key) })),
                    ]
                      // A SEAL item lists its fitted seals under usedSealNumbers; its own sealNumbers is always empty.
                      .filter((item) => !(isSealType(activeItem.itemType) && (item.key === "sealNumbers" || item.key === "seal_numbers")))
                      .map((item) => {
                      if (SEAL_LIST_KEYS.has(item.key)) {
                        const isSealItem = isSealType(activeItem.itemType);
                        return (
                          <SealNumbersPanel
                            key={item.key}
                            className="sm:col-span-2"
                            label={item.label}
                            seals={sealNumberList(item.value === EMPTY_VALUE ? [] : item.value)}
                            infoTitle={isSealItem ? t.inventorySealRangeFittedInfoTitle : t.inventorySealNumbersInfoTitle}
                            infoDescription={isSealItem ? t.inventorySealRangeFittedInfo : t.inventorySealNumbersInfo}
                            emptyText={t.inventorySealNoneFitted}
                            testId="inventory-details-seal-numbers"
                          />
                        );
                      }
                      const Icon = item.icon;
                      const value = displayValue(item.value);
                      return (
                        <div
                          key={item.key}
                          className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3"
                        >
                          <div className="h-10 w-10 rounded-xl bg-[#E8F1FB] text-[#236DB4] flex items-center justify-center">
                            <Icon size={18} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                              {item.label}
                            </div>
                            <div className="mt-0.5 text-sm font-semibold text-slate-800 truncate" title={String(value)}>
                              {value}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Replacement & Connected Components -- hidden for now,
                      only Inventory Details should show in this panel.
                  <div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
                      {t.inventoryReplacementSection}
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="text-sm font-semibold text-slate-800">{t.inventoryValueOldMeter}</div>
                        <div className="text-xs text-slate-500">
                          {displayValue(activeItem.replacedMeterId || activeItem.oldMeterId)}
                        </div>
                        <span className="mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600">
                          {formatStatusLabel(activeItem.replacedStatus || "REMOVED")}
                        </span>
                      </div>
                      <div className="rounded-xl border border-[#CFE3FB] bg-[#E8F1FB] p-3">
                        <div className="text-sm font-semibold text-[#236DB4]">{t.inventoryValueNewMeter}</div>
                        <div className="text-xs text-[#236DB4]">
                          {displayValue(activeItem.identifier || activeItem.meterBarCode || activeItem.id)}
                        </div>
                        <span className="mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-[#E8F1FB] text-[#236DB4]">
                          {formatStatusLabel(activeItem.status || "INSTALLED")}
                        </span>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="text-sm font-semibold text-slate-800">{t.inventoryValueModem}</div>
                        <div className="text-xs text-slate-500">
                          {displayValue(activeItem.modemNo || activeItem.metadata?.modemNo)}
                        </div>
                        <span className="mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600">
                          {formatStatusLabel(activeItem.modemStatus || activeItem.status)}
                        </span>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="text-sm font-semibold text-slate-800">{t.inventoryValueSimCard}</div>
                        <div className="text-xs text-slate-500">
                          {displayValue(activeItem.simNo || activeItem.metadata?.simNo)}
                        </div>
                        <span className="mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600">
                          {formatStatusLabel(activeItem.simStatus || activeItem.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  */}
                </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
